import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ChevronLeft, ChevronRight, Plus, Settings2, EyeOff, Eye, Search, X,
  Clock, User, Calendar, Receipt, FileText, ExternalLink, RefreshCw,
  AlertCircle, Mic, CheckCircle2, FileCheck, AlertTriangle, Send, CircleDot,
  Pencil, CalendarPlus, XCircle, CreditCard, ArrowRight, Download, Loader2
} from "lucide-react";
import PatientFormsSection from "@/components/PatientFormsSection";
import { FormReviewPanel } from "@/components/FormReviewPanel";
import GuardianDetailsPanel from "@/components/GuardianDetailsPanel";
import DeviceFormMode from "@/components/DeviceFormMode";
import PDFFormViewerWithAnnotations from "@/components/PDFFormViewerWithAnnotations";
import AuditTrailPanel, { ukDate } from "@/components/AuditTrailPanel";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7);
// Premium appointment-type colour palette — visibly distinct, clinic-grade
const TYPE_PALETTE = [
  { bg: "#D4EDEC", border: "#1A7A78", text: "#145C5B" },  // teal (initial)
  { bg: "#D6E4F0", border: "#3B6FA0", text: "#2A5178" },  // blue (follow-up)
  { bg: "#F0DDD0", border: "#B85C32", text: "#8A4425" },  // terracotta (assessment)
  { bg: "#E3D5F0", border: "#7B52A8", text: "#5A3A80" },  // violet (treatment)
  { bg: "#D5EDDA", border: "#3D8C50", text: "#2B6638" },  // green (review)
  { bg: "#F5E0C4", border: "#C48B3F", text: "#8E6428" },  // amber (massage)
  { bg: "#F0D4D8", border: "#B84A5C", text: "#8C3545" },  // rose (procedure)
  { bg: "#CCE8E6", border: "#2A8E8A", text: "#1E6B68" },  // ocean (rehab)
  { bg: "#E4D0F2", border: "#8C4DB8", text: "#6A3590" },  // plum (injection)
  { bg: "#D0D8F0", border: "#4A5CB8", text: "#354590" },  // indigo (consult)
];
const SB = { booked: "bg-[#1A1B1A]/5 text-[#757675]", arrived: "bg-amber-50 text-amber-600", waiting: "bg-amber-100 text-amber-700", "in-session": "bg-[#205B5A]/10 text-[#205B5A]", completed: "bg-[#205B5A]/10 text-[#205B5A]", cancelled: "bg-red-50 text-red-500", "no-show": "bg-red-50 text-red-400" };

function ds(d) { return d.toISOString().split("T")[0]; }
function ad(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function tm(t) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }
function getDays(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirst(y, m) { const d = new Date(y, m, 1).getDay(); return d === 0 ? 6 : d - 1; }

/* ── Mini Calendar ── */
function MiniCal({ selected, onSelect }) {
  const [d, setD] = useState(new Date(selected));
  const y = d.getFullYear(), m = d.getMonth();
  const days = getDays(y, m), first = getFirst(y, m);
  const todayS = ds(new Date());
  return (
    <div data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setD(new Date(y, m - 1, 1))} className="p-0.5"><ChevronLeft className="w-3 h-3 text-[#757675]" /></button>
        <span className="text-[10px] font-medium text-[#4A4B4A]">{d.toLocaleString("default", { month: "short", year: "numeric" })}</span>
        <button onClick={() => setD(new Date(y, m + 1, 1))} className="p-0.5"><ChevronRight className="w-3 h-3 text-[#757675]" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0">
        {["M","T","W","T","F","S","S"].map((d,i) => <div key={i} className="text-[8px] text-[#757675] text-center py-0.5">{d}</div>)}
        {Array.from({ length: first }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: days }, (_, i) => {
          const dateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
          const isSel = dateStr === selected, isToday = dateStr === todayS;
          return (
            <button key={i} onClick={() => onSelect(dateStr)}
              className={`text-[10px] w-6 h-6 rounded-full mx-auto flex items-center justify-center transition-colors ${isSel ? "bg-[#205B5A] text-white" : isToday ? "bg-[#205B5A]/10 text-[#205B5A] font-semibold" : "text-[#4A4B4A] hover:bg-[#FAF9F6]"}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Appointment Drawer ── */
function ApptDrawer({ apptId, onClose, onUpdated, onOpenNote, onCollectForms, onBookAnother, apptTypes = [], practitioners = [], clinicName = "" }) {
  const [appt, setAppt] = useState(null);
  const [note, setNote] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [patientCtx, setPatientCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [notifyPatient, setNotifyPatient] = useState(true);
  const [emailFeedback, setEmailFeedback] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [rescheduleForm, setRescheduleForm] = useState({ date: "", start_time: "" });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  // Device form mode state
  const [deviceFormData, setDeviceFormData] = useState(null); // { template, formAssignment }
  const [deviceFormError, setDeviceFormError] = useState("");
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const navigate = useNavigate();

  const loadInvoice = useCallback(async (patientId, appointmentId) => {
    try {
      const iRes = await api.get(`/invoices?patient_id=${patientId}&limit=50`);
      setInvoice(iRes.data.invoices.find(i => i.appointment_id === appointmentId) || null);
    } catch {}
  }, []);

  useEffect(() => {
    if (!apptId) return;
    setLoading(true);
    Promise.all([
      api.get(`/appointments/${apptId}`),
      api.get(`/clinical/notes/by-appointment/${apptId}`).catch(() => ({ data: { note: null } })),
    ]).then(([aRes, nRes]) => {
      setAppt(aRes.data);
      setNote(nRes.data.note);
      // Fetch patient context (alerts, flags)
      if (aRes.data.patient_id) {
        api.get(`/patients/${aRes.data.patient_id}`).then(pRes => setPatientCtx(pRes.data)).catch(() => {});
      }
      loadInvoice(aRes.data.patient_id, apptId);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [apptId, loadInvoice]);

  const updateStatus = async (status) => {
    await api.put(`/appointments/${apptId}`, { status });
    onUpdated();
    const { data } = await api.get(`/appointments/${apptId}`);
    setAppt(data);
  };

  const createInvoice = async () => {
    const { data } = await api.post(`/appointments/${apptId}/invoice`);
    setInvoice(data);
  };

  const resendForms = async () => {
    await api.post(`/appointments/${apptId}/resend-forms`);
    const { data } = await api.get(`/appointments/${apptId}`);
    setAppt(data);
  };

  // Open device form mode for a specific form assignment
  const openDeviceForm = async (formAssignment) => {
    try {
      const { data: template } = await api.get(`/forms/templates/${formAssignment.template_id}`);
      setDeviceFormData({
        template,
        formAssignment,
        formAssignmentId: formAssignment.id,
        patientId: appt.patient_id,
        appointmentId: appt.id,
        patientName: appt.patient?.first_name + " " + appt.patient?.last_name,
      });
    } catch (err) {
      console.error("Failed to load form template:", err);
      setDeviceFormError("This form could not be opened. Please refresh and try again.");
      setTimeout(() => setDeviceFormError(""), 6000);
    }
  };

  // Handle device form completion
  const handleDeviceFormComplete = async () => {
    setDeviceFormData(null);
    // Refresh appointment to get updated form status
    const { data } = await api.get(`/appointments/${apptId}`);
    setAppt(data);
    onUpdated?.();
  };

  // View submission details
  const handleViewSubmission = async (submission) => {
    try {
      const { data } = await api.get(`/forms/submissions/${submission.id}`);
      setViewingSubmission(data);
    } catch (err) {
      console.error("Failed to load submission:", err);
    }
  };

  const confirmPayment = async () => {
    if (!invoice) return;
    setPaymentLoading(true);
    setPaymentError("");
    try {
      await api.put(`/invoices/${invoice.id}`, { 
        status: "paid", 
        paid_amount: invoice.total,
        payment_method: paymentMethod,
        payment_notes: paymentNotes || undefined
      });
      // Refresh invoice state immediately
      await loadInvoice(appt.patient_id, apptId);
      // Refresh patient context to update outstanding balance
      if (appt.patient_id) {
        api.get(`/patients/${appt.patient_id}`).then(pRes => setPatientCtx(pRes.data)).catch(() => {});
      }
      setShowPaymentModal(false);
      setPaymentNotes("");
      onUpdated(); // Trigger global refresh
    } catch (err) {
      console.error("Payment error:", err);
      setPaymentError("Failed to process payment. Please try again.");
    }
    setPaymentLoading(false);
  };

  const handleEdit = () => {
    setEditForm({
      appointment_type_name: appt.appointment_type_name || "",
      duration_minutes: appt.duration_minutes || 30,
      notes: appt.notes || "",
      practitioner_id: appt.practitioner?.id || appt.practitioner_id || "",
      date: appt.date || "",
      start_time: appt.start_time || "",
      price: appt.price || 0,
    });
    setShowEdit(true);
  };

  // Calculate end time from start and duration
  const calcEndTime = (startTime, duration) => {
    if (!startTime) return "";
    const [h, m] = startTime.split(":").map(Number);
    const totalMin = h * 60 + m + duration;
    return `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;
  };

  // Handle appointment type change - auto-fill duration and price
  const handleTypeChange = (typeName) => {
    const type = apptTypes.find(t => t.name === typeName);
    setEditForm(f => ({
      ...f,
      appointment_type_name: typeName,
      duration_minutes: type?.duration_minutes || f.duration_minutes,
      price: type?.price ?? f.price,
    }));
  };

  const saveEdit = async () => {
    // Calculate end_time based on start_time and duration
    const endTime = calcEndTime(editForm.start_time, editForm.duration_minutes);
    const payload = {
      ...editForm,
      end_time: endTime,
    };
    await api.put(`/appointments/${apptId}`, payload);
    setShowEdit(false);
    onUpdated();
    const { data } = await api.get(`/appointments/${apptId}`);
    setAppt(data);
  };

  const handleReschedule = () => {
    setRescheduleForm({ date: appt.date, start_time: appt.start_time });
    setShowReschedule(true);
  };

  const saveReschedule = async () => {
    const dur = appt.duration_minutes || 30;
    const startMin = parseInt(rescheduleForm.start_time.split(":")[0]) * 60 + parseInt(rescheduleForm.start_time.split(":")[1]);
    const endMin = startMin + dur;
    const newEnd = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    try {
      const { data } = await api.post(`/appointments/${apptId}/reschedule`, {
        date: rescheduleForm.date, start_time: rescheduleForm.start_time, end_time: newEnd,
        notify_patient: notifyPatient && !!appt.patient?.email,
      });
      setEmailFeedback({
        ok: ["sent", "mock", "queued", "not_requested", "no_email"].includes(data.email_status),
        message: data.message,
        canRetry: data.can_retry,
        kind: "rescheduled",
      });
      setShowReschedule(false);
      onUpdated();
      const refreshed = await api.get(`/appointments/${apptId}`);
      setAppt(refreshed.data);
    } catch (e) {
      setEmailFeedback({ ok: false, message: e.response?.data?.detail || "Could not reschedule the appointment." });
    }
  };

  const retryNotification = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/appointments/${apptId}/retry-notification`,
        { kind: emailFeedback?.kind || "cancelled" });
      setEmailFeedback({
        ok: data.email_status !== "failed",
        message: data.message,
        canRetry: data.can_retry,
        kind: emailFeedback?.kind || "cancelled",
      });
    } catch (e) {
      setEmailFeedback({
        ok: false,
        message: e.response?.data?.detail || "Could not retry the notification.",
        canRetry: true,
        kind: emailFeedback?.kind || "cancelled",
      });
    }
    setRetrying(false);
  };

  const handleCancel = async () => {
    try {
      const { data } = await api.post(`/appointments/${apptId}/cancel`, {
        reason: cancelReason || null,
        notify_patient: notifyPatient && !!appt.patient?.email,
      });
      setShowCancelConfirm(false);
      onUpdated();
      const refreshed = await api.get(`/appointments/${apptId}`);
      setAppt(refreshed.data);
      setEmailFeedback({
        ok: data.email_status !== "failed",
        message: data.message,
        canRetry: data.can_retry,
        kind: "cancelled",
      });
    } catch (e) {
      setEmailFeedback({ ok: false, message: e.response?.data?.detail || "Could not cancel the appointment." });
    }
  };

  const NotifyPatientToggle = () => (
    appt?.patient?.email ? (
      <label className="flex items-center gap-2 mb-2 cursor-pointer" data-testid="notify-patient-wrap">
        <input type="checkbox" checked={notifyPatient} onChange={e => setNotifyPatient(e.target.checked)}
          data-testid="notify-patient-checkbox" className="w-3.5 h-3.5 accent-[#205B5A]" />
        <span className="text-[10px] text-[#4A4B4A]">Notify patient by email ({appt.patient.email})</span>
      </label>
    ) : (
      <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2"
        data-testid="notify-patient-no-email">
        Patient has no email address saved.
      </p>
    )
  );

  if (!apptId) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="appointment-drawer">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        {loading ? (
          <div className="p-6"><div className="animate-pulse space-y-3"><div className="h-6 bg-[#1A1B1A]/5 rounded w-40" /><div className="h-4 bg-[#1A1B1A]/5 rounded w-60" /></div></div>
        ) : appt ? (
          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>
                  {appt.patient?.first_name} {appt.patient?.last_name}
                </h2>
                <p className="text-xs text-[#757675]">{appt.appointment_type_name} · {appt.date}</p>
                <p className="text-xs text-[#757675] font-mono">{appt.start_time} – {appt.end_time} ({appt.duration_minutes} min)</p>
              </div>
              <div className="flex items-center gap-2">
                {appt.needs_practitioner_review && (
                  <span className="text-[9px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700"
                    data-testid="drawer-needs-review-badge" title="This practitioner is no longer active - reassign or cancel">
                    Needs review
                  </span>
                )}
                <span className={`text-[9px] font-semibold px-2.5 py-0.5 rounded-full capitalize ${SB[appt.status] || ""}`}>{appt.status}</span>
                <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-[#FAF9F6] rounded-xl p-3.5 mb-3 space-y-2">
              <div className="flex items-center gap-2 text-xs"><User className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Practitioner</span><span className="ml-auto font-medium text-[#1A1B1A]">{appt.practitioner?.full_name}</span></div>
              {appt.patient?.date_of_birth && <div className="flex items-center gap-2 text-xs"><span className="text-[#757675]">DOB / Age</span><span className="ml-auto font-medium text-[#1A1B1A]">{appt.patient.date_of_birth} ({Math.floor((Date.now() - new Date(appt.patient.date_of_birth).getTime()) / 31557600000)}y)</span></div>}
              {appt.patient?.phone && <div className="flex items-center gap-2 text-xs"><span className="text-[#757675]">Phone</span><span className="ml-auto font-medium text-[#1A1B1A]">{appt.patient.phone}</span></div>}
              {appt.patient?.email && <div className="flex items-center gap-2 text-xs"><span className="text-[#757675]">Email</span><span className="ml-auto font-medium text-[#1A1B1A] truncate max-w-[180px]">{appt.patient.email}</span></div>}
              {appt.price > 0 && <div className="flex items-center gap-2 text-xs"><Receipt className="w-3 h-3 text-[#757675]" /><span className="text-[#757675]">Price</span><span className="ml-auto font-semibold text-[#205B5A]">£{appt.price?.toFixed(2)}</span></div>}
            </div>

            {/* Patient Context — alerts, flags, balance */}
            {patientCtx && (patientCtx.alerts?.length > 0 || patientCtx.flags?.length > 0 || patientCtx.account?.balance > 0) && (
              <div className="mb-3 space-y-2" data-testid="drawer-patient-context">
                {patientCtx.alerts?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {patientCtx.alerts.map(a => (
                      <div key={a.id} className="flex items-center gap-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                        <span className="text-[10px] font-semibold text-red-700">{a.alert_type}</span>
                        <span className="text-[10px] text-red-600">— {a.description}</span>
                      </div>
                    ))}
                  </div>
                )}
                {patientCtx.flags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">{patientCtx.flags.map(f => <span key={f.id} className="text-[9px] font-medium bg-[#205B5A]/10 text-[#205B5A] px-2 py-0.5 rounded-full">{f.label}</span>)}</div>
                )}
                {patientCtx.account?.balance > 0 && (
                  <div className="flex items-center gap-2 text-[10px] bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                    <Receipt className="w-3 h-3 text-amber-500" />
                    <span className="text-amber-700">Outstanding balance: <strong>£{patientCtx.account.balance.toFixed(2)}</strong></span>
                  </div>
                )}
              </div>
            )}

            {/* Forms Section - Always visible */}
            <PatientFormsSection
              appointmentId={appt.id}
              patientId={appt.patient_id}
              patientName={appt.patient?.first_name + " " + appt.patient?.last_name}
              formAssignments={appt.form_assignments || []}
              formStatus={appt.form_status}
              minorInfo={appt.minor_info}
              patientEmail={appt.patient_email || appt.patient?.email}
              onRefresh={async () => {
                const { data } = await api.get(`/appointments/${apptId}`);
                setAppt(data);
              }}
              onOpenDeviceForm={openDeviceForm}
              onViewSubmission={handleViewSubmission}
            />
            {deviceFormError && (
              <p className="text-[10px] text-red-500 mb-4" data-testid="device-form-error">{deviceFormError}</p>
            )}

            {/* Note Status */}
            <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-xl p-3.5 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-3.5 h-3.5 text-[#205B5A]" />
                <span className="text-xs font-semibold text-[#1A1B1A]">Treatment note</span>
              </div>
              {note ? (
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${note.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"}`}>{note.status === "final" ? "Final" : "Draft"}</span>
                    <span className="text-[10px] text-[#757675] ml-2">{note.presenting_complaint ? note.presenting_complaint.slice(0, 40) + "..." : "Note started"}</span>
                  </div>
                  <button onClick={() => onOpenNote(appt, note)} data-testid="drawer-continue-note-btn" className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544]">{note.status === "final" ? "View" : "Continue"}</button>
                </div>
              ) : (
                <button onClick={() => onOpenNote(appt, null)} data-testid="drawer-new-note-btn" className="text-[10px] font-medium text-[#205B5A] bg-[#205B5A]/10 px-3 py-1 rounded-full hover:bg-[#205B5A]/15">New treatment note</button>
              )}
            </div>

            {/* Invoice & Payment Status */}
            {invoice && (
              <div className="bg-white border border-[#1A1B1A]/[0.06] rounded-xl p-3.5 mb-4" data-testid="drawer-invoice-section">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="w-3.5 h-3.5 text-[#205B5A]" />
                  <span className="text-xs font-semibold text-[#1A1B1A]">{invoice.invoice_number}</span>
                </div>
                
                {invoice.status === "paid" ? (
                  /* ── PAID STATE ── */
                  <div className="flex items-center justify-between bg-emerald-50/80 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <div>
                        <p className="text-xs font-semibold text-emerald-700">Paid</p>
                        {invoice.payment_method && (
                          <p className="text-[10px] text-emerald-600 capitalize">{invoice.payment_method.replace(/_/g, " ")}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-emerald-700">£{invoice.total?.toFixed(2)}</span>
                  </div>
                ) : (
                  /* ── UNPAID STATE ── */
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Unpaid</span>
                        <span className="text-[10px] text-[#757675]">Outstanding</span>
                      </div>
                      <span className="text-sm font-semibold text-[#1A1B1A]">£{invoice.total?.toFixed(2)}</span>
                    </div>
                    {appt.status === "completed" && (
                      <button 
                        onClick={() => setShowPaymentModal(true)} 
                        data-testid="drawer-confirm-payment-btn"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#205B5A] text-white text-[11px] font-medium hover:bg-[#174544] transition-colors"
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        Confirm payment
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Completed without invoice alert */}
            {appt.status === "completed" && !invoice && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-4">
                <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] text-amber-700 flex-1">Not yet invoiced</span>
                <button onClick={createInvoice} data-testid="drawer-create-invoice-btn" className="text-[10px] font-medium text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">Create invoice</button>
              </div>
            )}

            {/* Status Actions */}
            <div className="space-y-1.5 mb-3">
              <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-1.5">Status</p>
              <div className="flex flex-wrap gap-1.5">
                {["booked", "scheduled", "confirmed"].includes(appt.status) && <>
                  <button onClick={() => updateStatus("arrived")} data-testid="drawer-arrived-btn" className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-600 hover:bg-amber-100">Mark as arrived</button>
                  <button onClick={() => updateStatus("no-show")} className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-[#1A1B1A]/5 text-[#757675] hover:bg-[#1A1B1A]/8">No-show</button>
                </>}
                {["arrived", "waiting", "in-session"].includes(appt.status) &&
                  <button onClick={() => updateStatus("completed")} data-testid="drawer-complete-btn" className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-[#205B5A] text-white hover:bg-[#174544]">Complete appointment</button>}
                {appt.status === "completed" &&
                  <span className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-[#205B5A]/10 text-[#205B5A]" data-testid="drawer-completed-badge">Completed</span>}
              </div>
            </div>

            {/* Manage */}
            {appt.status !== "cancelled" && (
              <div className="space-y-1 mb-3">
                <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-1.5">Manage</p>
                <div className="space-y-0.5">
                  <button onClick={handleEdit} data-testid="drawer-edit-btn" className="w-full text-left px-3 py-2 rounded-lg text-[11px] font-medium text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors"><Pencil className="w-3 h-3" /> Edit appointment</button>
                  {/* A completed appointment has happened: it cannot be moved or cancelled. */}
                  {appt.status !== "completed" && <>
                    <button onClick={handleReschedule} data-testid="drawer-reschedule-btn" className="w-full text-left px-3 py-2 rounded-lg text-[11px] font-medium text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors"><RefreshCw className="w-3 h-3" /> Reschedule</button>
                  </>}
                  <button onClick={() => onBookAnother && onBookAnother(appt)} data-testid="drawer-book-another-btn" className="w-full text-left px-3 py-2 rounded-lg text-[11px] font-medium text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors"><CalendarPlus className="w-3 h-3" /> Book another for this patient</button>
                  {appt.status !== "completed" && (
                    <button onClick={() => setShowCancelConfirm(true)} data-testid="drawer-cancel-btn" className="w-full text-left px-3 py-2 rounded-lg text-[11px] font-medium text-red-400 hover:bg-red-50 flex items-center gap-2 transition-colors"><XCircle className="w-3 h-3" /> Cancel appointment</button>
                  )}
                </div>
              </div>
            )}

            {/* Links */}
            <div className="space-y-0.5 border-t border-[#1A1B1A]/[0.06] pt-3">
              <button onClick={() => navigate(`/app/patients/${appt.patient_id}`)} data-testid="drawer-patient-link" className="w-full text-left px-3 py-2 rounded-lg text-[11px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2"><User className="w-3 h-3" /> Open patient profile <ChevronRight className="w-3 h-3 ml-auto text-[#757675]" /></button>
              <button onClick={() => navigate(`/app/appointments/${apptId}`)} data-testid="drawer-full-page-link" className="w-full text-left px-3 py-2 rounded-lg text-[11px] text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2"><ExternalLink className="w-3 h-3" /> Full appointment page <ChevronRight className="w-3 h-3 ml-auto text-[#757675]" /></button>
            </div>

            {/* Inline Edit Form */}
            {showEdit && (
              <div className="mt-3 bg-[#FAF9F6] rounded-xl p-4 border border-[#1A1B1A]/[0.06]" data-testid="drawer-edit-form">
                <p className="text-xs font-semibold text-[#1A1B1A] mb-3">Edit appointment</p>
                <div className="space-y-3">
                  {/* Appointment Type Dropdown */}
                  <div>
                    <label className="text-[10px] text-[#757675] mb-1 block">Appointment type</label>
                    <select 
                      value={editForm.appointment_type_name} 
                      onChange={e => handleTypeChange(e.target.value)}
                      data-testid="edit-appointment-type"
                      className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white"
                    >
                      {apptTypes.map(t => (
                        <option key={t.id} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Practitioner Dropdown */}
                  <div>
                    <label className="text-[10px] text-[#757675] mb-1 block">Practitioner</label>
                    <select 
                      value={editForm.practitioner_id} 
                      onChange={e => setEditForm({...editForm, practitioner_id: e.target.value})}
                      data-testid="edit-practitioner"
                      className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white"
                    >
                      {practitioners.filter(p => p.is_active_practitioner !== false || p.id === editForm.practitioner_id).map(p => (
                        <option key={p.id} value={p.id}>
                          {p.full_name}{p.is_active_practitioner === false ? " (former practitioner)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#757675] mb-1 block">Date</label>
                      <input 
                        type="date" 
                        value={editForm.date} 
                        onChange={e => setEditForm({...editForm, date: e.target.value})}
                        data-testid="edit-date"
                        className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white" 
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#757675] mb-1 block">Start time</label>
                      <input 
                        type="time" 
                        value={editForm.start_time} 
                        onChange={e => setEditForm({...editForm, start_time: e.target.value})}
                        data-testid="edit-start-time"
                        className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white" 
                      />
                    </div>
                  </div>

                  {/* Duration and Price */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#757675] mb-1 block">Duration</label>
                      <select 
                        value={editForm.duration_minutes} 
                        onChange={e => setEditForm({...editForm, duration_minutes: Number(e.target.value)})}
                        data-testid="edit-duration"
                        className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white"
                      >
                        {[15, 20, 30, 45, 60, 75, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#757675] mb-1 block">Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#757675]">£</span>
                        <input 
                          type="number" 
                          step="0.01"
                          value={editForm.price} 
                          onChange={e => setEditForm({...editForm, price: Number(e.target.value)})}
                          data-testid="edit-price"
                          className="w-full pl-7 pr-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A] bg-white" 
                        />
                      </div>
                    </div>
                  </div>

                  {/* End Time Preview */}
                  <div className="bg-white rounded-lg px-3 py-2 border border-[#1A1B1A]/[0.06]">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#757675]">End time</span>
                      <span className="text-xs font-medium text-[#1A1B1A] font-mono">
                        {calcEndTime(editForm.start_time, editForm.duration_minutes) || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-[10px] text-[#757675] mb-1 block">Notes</label>
                    <textarea 
                      value={editForm.notes} 
                      onChange={e => setEditForm({...editForm, notes: e.target.value})} 
                      rows={2} 
                      placeholder="Appointment notes..." 
                      data-testid="edit-notes"
                      className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs resize-none focus:outline-none focus:border-[#205B5A] bg-white" 
                    />
                  </div>

                  {/* Invoice warning */}
                  {invoice && invoice.status === "paid" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-amber-700">
                        <strong>Note:</strong> This appointment has a paid invoice. Price changes here won't affect the existing invoice.
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button 
                      onClick={saveEdit} 
                      data-testid="drawer-save-edit-btn" 
                      className="flex-1 text-xs font-medium text-white bg-[#205B5A] px-3 py-2 rounded-lg hover:bg-[#174544] transition-colors"
                    >
                      Save changes
                    </button>
                    <button 
                      onClick={() => setShowEdit(false)} 
                      className="px-4 py-2 text-xs text-[#757675] hover:text-[#1A1B1A] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {emailFeedback && (
              <div className={`mt-3 rounded-xl px-3.5 py-2.5 flex items-start gap-2 ${emailFeedback.ok ? "bg-[#205B5A]/10" : "bg-red-50 border border-red-200"}`}
                data-testid="appointment-email-feedback">
                {emailFeedback.ok
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-[#205B5A] mt-0.5 flex-shrink-0" />
                  : <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />}
                <div className="flex-1">
                  <p className={`text-[11px] ${emailFeedback.ok ? "text-[#205B5A]" : "text-red-600"}`}>{emailFeedback.message}</p>
                  {emailFeedback.canRetry && (
                    <button onClick={retryNotification} disabled={retrying}
                      data-testid="retry-notification-btn"
                      className="mt-2 text-[11px] px-2.5 py-1 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">
                      {retrying ? "Retrying..." : "Retry notification"}
                    </button>
                  )}
                </div>
                <button onClick={() => setEmailFeedback(null)} data-testid="dismiss-email-feedback-btn"
                  className="ml-auto text-[10px] text-[#757675]">Dismiss</button>
              </div>
            )}

            {/* Inline Reschedule Form */}
            {showReschedule && (
              <div className="mt-3 bg-[#FAF9F6] rounded-xl p-3.5 border border-[#1A1B1A]/[0.06]">
                <p className="text-xs font-semibold text-[#1A1B1A] mb-2">Reschedule</p>
                <div className="space-y-2">
                  <input type="date" value={rescheduleForm.date} onChange={e => setRescheduleForm({...rescheduleForm, date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" />
                  <input type="time" value={rescheduleForm.start_time} onChange={e => setRescheduleForm({...rescheduleForm, start_time: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" />
                  <NotifyPatientToggle />
                  <div className="flex gap-2"><button onClick={saveReschedule} data-testid="drawer-save-reschedule-btn" className="text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-lg hover:bg-[#174544]">Reschedule</button><button onClick={() => setShowReschedule(false)} className="text-xs text-[#757675]">Cancel</button></div>
                </div>
              </div>
            )}

            {/* Cancel Confirmation */}
            {showCancelConfirm && (
              <div className="mt-3 bg-red-50 rounded-xl p-3.5 border border-red-200">
                <p className="text-xs font-semibold text-red-700 mb-2">Cancel this appointment?</p>
                <p className="text-[10px] text-red-600 mb-2">This will cancel the appointment for {appt.patient?.first_name} {appt.patient?.last_name}.</p>
                <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2} placeholder="Cancellation reason (optional)" className="w-full px-3 py-2 rounded-lg border border-red-200 text-xs resize-none mb-2 bg-white" data-testid="drawer-cancel-reason" />
                <NotifyPatientToggle />
                <div className="flex gap-2"><button onClick={handleCancel} data-testid="drawer-confirm-cancel-btn" className="text-xs font-medium text-white bg-red-500 px-3 py-1.5 rounded-lg hover:bg-red-600">Confirm cancel</button><button onClick={() => setShowCancelConfirm(false)} className="text-xs text-[#757675]">Keep appointment</button></div>
              </div>
            )}

            {/* Payment Confirmation Modal */}
            {showPaymentModal && invoice && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="payment-modal" onClick={() => { setShowPaymentModal(false); setPaymentNotes(""); setPaymentError(""); }}>
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-5 animate-scale-in" onClick={e => e.stopPropagation()}>
                  <h3 className="text-base font-medium text-[#1A1B1A] mb-1" style={H}>Confirm payment</h3>
                  <p className="text-[11px] text-[#757675] mb-4">Record payment for invoice {invoice.invoice_number}</p>
                  
                  <div className="bg-[#FAF9F6] rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
                    <span className="text-xs text-[#757675]">Amount</span>
                    <span className="text-lg font-semibold text-[#1A1B1A]" style={H}>£{invoice.total?.toFixed(2)}</span>
                  </div>

                  <div className="mb-4">
                    <label className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2 block">Payment method</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: "card", label: "Card", icon: "💳" },
                        { id: "cash", label: "Cash", icon: "💵" },
                        { id: "bank_transfer", label: "Bank transfer", icon: "🏦" },
                        { id: "other", label: "Other", icon: "📝" },
                      ].map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setPaymentMethod(m.id)}
                          data-testid={`payment-method-${m.id}`}
                          className={`px-3 py-2.5 rounded-lg text-[11px] font-medium border transition-all flex items-center gap-2 ${
                            paymentMethod === m.id 
                              ? "border-[#205B5A] bg-[#205B5A]/5 text-[#205B5A]" 
                              : "border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6]"
                          }`}
                        >
                          <span className="text-base">{m.icon}</span>
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-1.5 block">Reference / notes <span className="font-normal lowercase">(optional)</span></label>
                    <input
                      type="text"
                      value={paymentNotes}
                      onChange={e => setPaymentNotes(e.target.value)}
                      placeholder="Transaction ref, receipt no, etc."
                      className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A]"
                      data-testid="payment-notes-input"
                    />
                  </div>

                  {paymentError && (
                    <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-[11px] text-red-600">
                      {paymentError}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={confirmPayment}
                      disabled={paymentLoading}
                      data-testid="payment-confirm-btn"
                      className="flex-1 py-2.5 rounded-lg bg-[#205B5A] text-white text-xs font-medium hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {paymentLoading ? "Processing..." : "Confirm payment"}
                    </button>
                    <button
                      onClick={() => { setShowPaymentModal(false); setPaymentNotes(""); setPaymentError(""); }}
                      className="px-4 py-2.5 rounded-lg border border-[#1A1B1A]/10 text-xs text-[#757675] hover:bg-[#FAF9F6] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Device Form Mode Overlay */}
      {deviceFormData && !deviceFormData.template?.is_uploaded_pdf && (
        <DeviceFormMode
          template={deviceFormData.template}
          patientId={deviceFormData.patientId}
          appointmentId={deviceFormData.appointmentId}
          patientName={deviceFormData.patientName}
          clinicName={clinicName}
          minorInfo={appt?.minor_info}
          onComplete={handleDeviceFormComplete}
          onCancel={() => setDeviceFormData(null)}
        />
      )}

      {/* PDF Form Viewer Overlay with Annotations */}
      {deviceFormData && deviceFormData.template?.is_uploaded_pdf && (
        <PDFFormViewerWithAnnotations
          template={deviceFormData.template}
          formAssignmentId={deviceFormData.formAssignmentId}
          patientId={deviceFormData.patientId}
          appointmentId={deviceFormData.appointmentId}
          patientName={deviceFormData.patientName}
          clinicName={clinicName}
          minorInfo={appt?.minor_info}
          onComplete={handleDeviceFormComplete}
          onCancel={() => setDeviceFormData(null)}
        />
      )}

      {/* Submission Detail Viewer Overlay */}
      {viewingSubmission && (
        <SubmissionViewer
          submission={viewingSubmission}
          onClose={() => setViewingSubmission(null)}
        />
      )}
    </div>
  );
}

/* ── Submission Viewer (for viewing completed forms in drawer) ── */
function SubmissionViewer({ submission: submissionProp, onClose }) {
  const [reviewedSubmission, setReviewedSubmission] = useState(null);
  const submission = reviewedSubmission || submissionProp;
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const template = submission?.template || submission?.template_snapshot;
  const fields = template?.fields || [];
  const responses = submission?.responses || {};
  const isPDF = template?.is_uploaded_pdf;
  const hasFlattened = Boolean(submission?.flattened_pdf_path);

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // Sanitize filename: remove special chars, replace spaces with underscores
  const sanitizeFilename = (str) => {
    if (!str) return "document";
    return str
      .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special chars except space and hyphen
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .replace(/-+/g, "-") // Normalize hyphens
      .replace(/_+/g, "_") // Normalize underscores
      .toLowerCase()
      .slice(0, 50); // Limit length
  };

  // Generate safe download filename: patient-name_form-name_YYYY-MM-DD.pdf
  const generateDownloadFilename = () => {
    const patientName = sanitizeFilename(submission?.patient_name || "patient");
    const formName = sanitizeFilename(template?.name || "form");
    
    // Get date from submission or use current date
    let dateStr;
    if (submission?.date_signed) {
      // date_signed is in DD/MM/YYYY format, convert to YYYY-MM-DD
      const parts = submission.date_signed.split("/");
      if (parts.length === 3) {
        dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        dateStr = new Date().toISOString().split("T")[0];
      }
    } else if (submission?.created_at) {
      dateStr = new Date(submission.created_at).toISOString().split("T")[0];
    } else {
      dateStr = new Date().toISOString().split("T")[0];
    }
    
    return `${patientName}_${formName}_${dateStr}.pdf`;
  };

  // Download flattened PDF with proper filename
  const handleDownloadPdf = async () => {
    if (!submission?.id || !hasFlattened) return;
    
    setDownloadLoading(true);
    setDownloadError(null);
    
    try {
      const response = await api.get(`/forms/submissions/${submission.id}/flattened-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = generateDownloadFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
    } catch (err) {
      console.error("Failed to download PDF:", err);
      setDownloadError("Failed to download PDF. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Fetch PDF via authenticated API and create blob URL
  // For completed submissions, load the flattened PDF; otherwise load original template
  useEffect(() => {
    let blobUrl = null;
    const fetchPdf = async () => {
      if (!isPDF || !template?.id) return;
      
      setPdfLoading(true);
      setPdfError(false);
      
      try {
        // Check if this is a completed submission with a flattened PDF
        const hasFlattened = submission?.flattened_pdf_path;
        
        let response;
        if (hasFlattened) {
          // Load the flattened/completed PDF
          response = await api.get(`/forms/submissions/${submission.id}/flattened-pdf`, {
            responseType: 'blob'
          });
        } else {
          // Fall back to original template PDF
          response = await api.get(`/forms/pdf/${template.id}`, {
            responseType: 'blob'
          });
        }
        
        blobUrl = URL.createObjectURL(response.data);
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        console.error("Failed to load PDF:", err);
        setPdfError(true);
      } finally {
        setPdfLoading(false);
      }
    };
    
    fetchPdf();
    
    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isPDF, template?.id, submission?.id, submission?.flattened_pdf_path]);

  return (
    <div className="fixed inset-0 z-[100] bg-[#FAF9F6] flex flex-col" data-testid="submission-viewer">
      {/* Header */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]" data-testid="close-submission-viewer">
            <X className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              {isPDF && (
                <div className="w-5 h-5 rounded bg-red-50 flex items-center justify-center" title="PDF Form">
                  <FileText className="w-3 h-3 text-red-500" />
                </div>
              )}
              <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>
                {template?.name || "Form Submission"}
              </h2>
            </div>
            <p className="text-xs text-[#757675]">
              {submission?.patient_name || "Patient"} · {formatDateTime(submission?.created_at)}
            </p>
          </div>
        </div>
        {submission?.signature_type && (
          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${
            submission.signature_type === "drawn" 
              ? "bg-[#205B5A]/10 text-[#205B5A]" 
              : "bg-[#496275]/10 text-[#496275]"
          }`}>
            {submission.signature_type === "drawn" ? "Drawn signature" : "Typed signature"}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6">
          
          {/* PDF Viewer Section (for PDF forms) */}
          {isPDF && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Document</h4>
              {pdfLoading ? (
                <div className="border border-[#1A1B1A]/10 rounded-xl bg-[#3d3d3d] flex items-center justify-center" style={{ height: "400px" }}>
                  <div className="text-center">
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-white/60 text-xs">Loading PDF...</p>
                  </div>
                </div>
              ) : pdfBlobUrl ? (
                <>
                  <div className="border border-[#1A1B1A]/10 rounded-xl overflow-hidden bg-white">
                    <object
                      data={`${pdfBlobUrl}#toolbar=1&view=FitH`}
                      type="application/pdf"
                      className="w-full"
                      style={{ height: "500px" }}
                      title={template?.name || "PDF Document"}
                    >
                      <p className="p-4 text-center text-[#757675]">
                        Your browser cannot display the PDF inline. 
                        <button
                          onClick={() => window.open(pdfBlobUrl, '_blank')}
                          className="text-[#205B5A] hover:underline ml-1"
                        >
                          Click here to open it in a new tab
                        </button>
                      </p>
                    </object>
                  </div>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <button
                      onClick={() => window.open(pdfBlobUrl, '_blank')}
                      className="inline-flex items-center gap-1.5 text-xs text-[#205B5A] hover:text-[#174544]"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open PDF in new tab
                    </button>
                    
                    {/* Download Completed PDF Button - only for flattened submissions */}
                    {hasFlattened && (
                      <button
                        onClick={handleDownloadPdf}
                        disabled={downloadLoading}
                        data-testid="download-completed-pdf-btn"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-full hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {downloadLoading ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-3 h-3" />
                            Download completed PDF
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Download error message */}
                  {downloadError && (
                    <div className="mt-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                      {downloadError}
                    </div>
                  )}
                </>
              ) : pdfError ? (
                <div className="border border-[#1A1B1A]/10 rounded-xl bg-[#FAF9F6] flex items-center justify-center p-8" style={{ minHeight: "200px" }}>
                  <div className="text-center">
                    <FileText className="w-8 h-8 text-[#757675]/40 mx-auto mb-2" />
                    <p className="text-sm text-[#757675]">Unable to load the PDF document.</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* PDF Acknowledgement/Consent Display */}
          {isPDF && (
            <div className="space-y-4 mb-6">
              <h4 className="text-xs font-semibold text-[#757675] uppercase tracking-wider">Acknowledgements</h4>
              
              {responses.acknowledgement && (
                <div className="flex items-start gap-2.5">
                  <div className="w-4 h-4 rounded border mt-0.5 flex items-center justify-center bg-[#205B5A] border-[#205B5A]">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                    </svg>
                  </div>
                  <span className="text-sm text-[#4A4B4A]">{responses.acknowledgement_text || "Acknowledged"}</span>
                </div>
              )}
              
              {responses.consent && (
                <div className="flex items-start gap-2.5">
                  <div className="w-4 h-4 rounded border mt-0.5 flex items-center justify-center bg-[#205B5A] border-[#205B5A]">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                    </svg>
                  </div>
                  <span className="text-sm text-[#4A4B4A]">{responses.consent_text || "Consented"}</span>
                </div>
              )}

              {responses.date_signed && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#757675]">Date signed:</span>
                  <span className="text-[#1A1B1A]">{ukDate(responses.date_signed)}</span>
                </div>
              )}
              {/* Also show submission-level date_signed if available (timezone-aware from backend) */}
              {submission?.date_signed && !responses.date_signed && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#757675]">Date signed:</span>
                  <span className="text-[#1A1B1A]">{ukDate(submission.date_signed)}</span>
                  {submission?.clinic_timezone && (
                    <span className="text-[10px] text-[#9A9B9A]">({submission.clinic_timezone})</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Form responses (for non-PDF forms) */}
          {!isPDF && (
            <div className="space-y-4">
              {fields.map((f, i) => (
                <div key={f.id || i}>
                  {f.field_type === "heading" && (
                    <h3 className="text-base font-medium text-[#1A1B1A] mt-4 mb-2" style={H}>{f.label}</h3>
                  )}
                  {f.field_type === "info_text" && (
                    <p className="text-sm text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">{f.label}</p>
                  )}
                  {(f.field_type === "short_text" || f.field_type === "long_text" || f.field_type === "date" || f.field_type === "dropdown" || f.field_type === "radio") && (
                    <div className="mb-3">
                      <label className="text-xs font-medium text-[#757675] mb-1 block">{f.label}</label>
                      <p className="text-sm text-[#1A1B1A] bg-[#FAF9F6] rounded-lg px-3 py-2.5">
                        {responses[f.id]
                          ? (f.field_type === "date" ? ukDate(responses[f.id]) : responses[f.id])
                          : <span className="text-[#757675] italic">Not provided</span>}
                      </p>
                    </div>
                  )}
                  {f.field_type === "checkbox" && (
                    <div className="mb-3 flex items-start gap-2.5">
                      <div className={`w-4 h-4 rounded border mt-0.5 flex items-center justify-center ${
                        responses[f.id] ? "bg-[#205B5A] border-[#205B5A]" : "border-[#1A1B1A]/20"
                      }`}>
                        {responses[f.id] && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                            <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-[#4A4B4A]">{f.label}</span>
                    </div>
                  )}
                  {f.field_type === "signature" && (
                    <div className="mb-3">
                      <label className="text-xs font-medium text-[#757675] mb-2 block">{f.label}</label>
                      <div className="border border-[#1A1B1A]/10 rounded-xl p-4 bg-white">
                        {submission.signature_type === "drawn" && submission.signature_data ? (
                          <div>
                            <img src={submission.signature_data} alt="Patient signature" className="max-h-32 w-auto" data-testid="viewer-drawn-signature" />
                            <p className="text-[10px] text-[#757675] mt-2 pt-2 border-t border-[#1A1B1A]/10">Drawn signature</p>
                          </div>
                        ) : submission.signature_type === "typed" && submission.typed_signature ? (
                          <div>
                            <p className="text-2xl text-[#1A1B1A] italic" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }} data-testid="viewer-typed-signature">
                              {submission.typed_signature}
                            </p>
                            <p className="text-[10px] text-[#757675] mt-2 pt-2 border-t border-[#1A1B1A]/10">Typed signature</p>
                          </div>
                        ) : submission.signature ? (
                          <div>
                            <p className="text-2xl text-[#1A1B1A] italic" style={{ fontFamily: "'Brush Script MT', cursive" }}>{submission.signature}</p>
                            <p className="text-[10px] text-[#757675] mt-2 pt-2 border-t border-[#1A1B1A]/10">Electronic signature</p>
                          </div>
                        ) : (
                          <p className="text-sm text-[#757675] italic">No signature provided</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Signature display (for PDF forms - signature is separate from fields) */}
          {isPDF && (submission.signature_type === "drawn" || submission.signature_type === "typed") && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Signature</h4>
              <div className="border border-[#1A1B1A]/10 rounded-xl p-4 bg-white">
                {submission.signature_type === "drawn" && submission.signature_data ? (
                  <div>
                    <img src={submission.signature_data} alt="Patient signature" className="max-h-32 w-auto" data-testid="viewer-drawn-signature" />
                    <p className="text-[10px] text-[#757675] mt-2 pt-2 border-t border-[#1A1B1A]/10">Drawn signature</p>
                  </div>
                ) : submission.signature_type === "typed" && submission.typed_signature ? (
                  <div>
                    <p className="text-2xl text-[#1A1B1A] italic" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }} data-testid="viewer-typed-signature">
                      {submission.typed_signature}
                    </p>
                    <p className="text-[10px] text-[#757675] mt-2 pt-2 border-t border-[#1A1B1A]/10">Typed signature</p>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Parent / guardian consent (under-18 patients) */}
          <GuardianDetailsPanel submission={submission} />

          {/* Staff review: accept / reject the submitted form */}
          <div className="mt-6">
            <FormReviewPanel submission={submission} onReviewed={(updated) => setReviewedSubmission({ ...submission, ...updated })} />
          </div>

          {/* Signature audit trail (IP masked; full details permission-gated) */}
          <AuditTrailPanel submission={submission} />
        </div>
      </div>
    </div>
  );
}

/* ── Note Editor Modal (Split Layout) ── */
function NoteEditor({ appt, existingNote, onClose, onSaved }) {
  const [form, setForm] = useState(existingNote || { appointment_type: appt?.appointment_type_name || "", status: "draft" });
  const [prevNotes, setPrevNotes] = useState([]);
  const [rightTab, setRightTab] = useState("previous");
  const [patientInfo, setPatientInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appt?.patient_id) {
      api.get(`/clinical/notes/${appt.patient_id}`).then(r => setPrevNotes(r.data.notes.filter(n => n.id !== existingNote?.id))).catch(() => {});
      api.get(`/patients/${appt.patient_id}`).then(r => setPatientInfo(r.data)).catch(() => {});
    }
  }, [appt?.patient_id, existingNote?.id]);

  const save = async (status) => {
    setSaving(true);
    const payload = { ...form, patient_id: appt.patient_id, appointment_id: appt.id, practitioner_id: appt.practitioner_id, status };
    try {
      if (existingNote?.id) {
        await api.put(`/clinical/notes/${existingNote.id}`, payload);
      } else {
        await api.post("/clinical/notes", payload);
      }
      onSaved();
    } catch {}
    setSaving(false);
  };

  const copyPrevious = (note) => {
    setForm(f => ({
      ...f,
      presenting_complaint: note.presenting_complaint || f.presenting_complaint,
      history: note.history || f.history,
      medical_history: note.medical_history || f.medical_history,
      medication: note.medication || f.medication,
      examination: note.examination || f.examination,
      treatment: note.treatment || f.treatment,
      advice: note.advice || f.advice,
      follow_up: note.follow_up || f.follow_up,
      body_regions: note.body_regions || f.body_regions,
    }));
  };

  const fields = [["presenting_complaint","Presenting complaint"],["history","History"],["medical_history","Medical history"],["medication","Medication"],["examination","Assessment"],["treatment","Treatment"],["advice","Advice"],["follow_up","Follow-up"],["outcome","Outcome"],["clinician_notes","Clinician notes"]];

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="note-editor">
      {/* Top bar */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
          <div>
            <h2 className="text-sm font-medium text-[#1A1B1A]" style={H}>{existingNote?.id ? "Edit treatment note" : "New treatment note"}</h2>
            <p className="text-[10px] text-[#757675]">{appt?.patient?.first_name} {appt?.patient?.last_name} · {appt?.date} {appt?.start_time}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${form.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"}`}>{form.status === "final" ? "Final" : "Draft"}</span>
          <button onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn" className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-4 py-1.5 rounded-full hover:bg-[#FAF9F6]">{saving ? "Saving..." : "Save draft"}</button>
          <button onClick={() => save("final")} disabled={saving} data-testid="finalize-note-btn" className="text-[11px] font-medium text-white bg-[#205B5A] px-4 py-1.5 rounded-full hover:bg-[#174544]">{saving ? "..." : "Finalize"}</button>
        </div>
      </div>

      {/* Split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Note editor */}
        <div className="flex-1 overflow-y-auto p-5 max-w-3xl">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] text-[#757675] mb-1 block">Type</label><input value={form.appointment_type || ""} onChange={e => setForm({...form, appointment_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] bg-white" /></div>
              <div><label className="text-[10px] text-[#757675] mb-1 block">Body regions</label><input value={form.body_regions?.join?.(", ") || ""} onChange={e => setForm({...form, body_regions: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} placeholder="e.g. Lumbar, Shoulder" className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] bg-white" /></div>
            </div>
            {fields.map(([k, l]) => (
              <div key={k}><label className="text-[10px] text-[#757675] mb-1 block">{l}</label><textarea value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})} rows={k === "presenting_complaint" || k === "examination" || k === "treatment" ? 3 : 2} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none bg-white" /></div>
            ))}
          </div>
        </div>

        {/* Right: Reference panel */}
        <div className="w-[320px] border-l border-[#1A1B1A]/[0.06] bg-white overflow-y-auto flex-shrink-0 hidden lg:block" data-testid="note-reference-panel">
          <div className="flex border-b border-[#1A1B1A]/[0.06]">
            {[{k:"previous",l:"Previous notes"},{k:"patient",l:"Patient"},{k:"appointments",l:"Appts"}].map(t => (
              <button key={t.k} onClick={() => setRightTab(t.k)}
                className={`flex-1 py-2.5 text-[10px] font-medium text-center border-b-2 -mb-px transition-colors ${rightTab === t.k ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent"}`}>{t.l}</button>
            ))}
          </div>

          <div className="p-3">
            {rightTab === "previous" && (
              <div className="space-y-2">
                {prevNotes.length === 0 ? <p className="text-xs text-[#757675] py-4 text-center">No previous notes</p> : prevNotes.slice(0, 10).map(n => (
                  <div key={n.id} className="bg-[#FAF9F6] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-[#1A1B1A]">{n.appointment_type || "Note"}</span>
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${n.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"}`}>{n.status}</span>
                    </div>
                    <p className="text-[10px] text-[#757675]">{n.practitioner_name} · {n.created_at?.split("T")[0]}</p>
                    {n.presenting_complaint && <p className="text-[10px] text-[#4A4B4A] mt-1.5 line-clamp-3">{n.presenting_complaint}</p>}
                    <button onClick={() => copyPrevious(n)} data-testid="copy-prev-note-btn" className="text-[9px] font-medium text-[#205B5A] mt-1.5 hover:text-[#174544]">Copy to current note</button>
                  </div>
                ))}
              </div>
            )}

            {rightTab === "patient" && patientInfo && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-[#1A1B1A]">{patientInfo.patient?.first_name} {patientInfo.patient?.last_name}</p>
                  <p className="text-[10px] text-[#757675]">DOB: {patientInfo.patient?.date_of_birth || "—"}</p>
                  <p className="text-[10px] text-[#757675]">{patientInfo.patient?.gender || "—"}</p>
                </div>
                {patientInfo.alerts?.length > 0 && (
                  <div>
                    <p className="text-[9px] font-semibold text-red-600 uppercase mb-1">Alerts</p>
                    {patientInfo.alerts.map(a => <div key={a.id} className="bg-red-50 rounded px-2 py-1 mb-1"><p className="text-[10px] font-medium text-red-700">{a.alert_type}</p><p className="text-[9px] text-red-600">{a.description}</p></div>)}
                  </div>
                )}
                {patientInfo.flags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">{patientInfo.flags.map(f => <span key={f.id} className="text-[9px] bg-[#205B5A]/10 text-[#205B5A] px-2 py-0.5 rounded-full">{f.label}</span>)}</div>
                )}
              </div>
            )}

            {rightTab === "appointments" && patientInfo && (
              <div className="space-y-1">
                {patientInfo.appointments?.slice(0, 10).map(a => (
                  <div key={a.id} className="bg-[#FAF9F6] rounded-lg p-2.5">
                    <p className="text-[10px] font-medium text-[#1A1B1A]">{a.date} {a.start_time}</p>
                    <p className="text-[9px] text-[#757675]">{a.appointment_type_name} · <span className="capitalize">{a.status}</span></p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Booking Modal ── */
function BookingModal({ date, time, practitioners, apptTypes, onClose, onCreated, prefillPatientId, prefillPatientName, onAddBlock }) {
  // Only active practitioners can take new bookings
  const bookable = practitioners.filter(p => p.is_active_practitioner !== false);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState(prefillPatientName || "");
  const [searching, setSearching] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newP, setNewP] = useState({ title: "", first_name: "", last_name: "", date_of_birth: "", email: "", phone: "" });
  const [form, setForm] = useState({ patient_id: prefillPatientId || "", practitioner_id: bookable[0]?.id || "", date: date || ds(new Date()), start_time: time || "09:00", end_time: "", appointment_type_name: apptTypes[0]?.name || "", duration_minutes: apptTypes[0]?.duration_minutes || 30, notes: "", price: apptTypes[0]?.price || 0, recurring: false, recurring_interval: "weekly", recurring_count: 1 });

  useEffect(() => { if (form.start_time && form.duration_minutes) { const m = tm(form.start_time) + form.duration_minutes; setForm(f => ({ ...f, end_time: `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}` })); } }, [form.start_time, form.duration_minutes]);

  const searchP = useCallback(async (q) => { if (q.length < 2) { setPatients([]); return; } setSearching(true); try { const { data } = await api.get(`/patients?search=${q}&limit=8`); setPatients(data.patients); } catch {} setSearching(false); }, []);
  useEffect(() => { const t = setTimeout(() => searchP(search), 300); return () => clearTimeout(t); }, [search, searchP]);

  const selType = (name) => { const t = apptTypes.find(a => a.name === name); setForm(f => ({ ...f, appointment_type_name: name, duration_minutes: t?.duration_minutes || 30, price: t?.price || 0 })); };
  const createPat = async () => { try { const { data } = await api.post("/patients", newP); setForm(f => ({ ...f, patient_id: data.id })); setSearch(`${data.first_name} ${data.last_name}`); setShowNew(false); setPatients([]); } catch {} };
  const submit = async (e) => { e.preventDefault(); try { const p = { ...form }; if (!p.recurring) { delete p.recurring_interval; delete p.recurring_count; } const { data } = await api.post("/appointments", p); onCreated(data); } catch {} };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="booking-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5"><h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Book appointment</h2><button onClick={onClose}><X className="w-4 h-4 text-[#757675]" /></button></div>
        <form onSubmit={submit} className="space-y-4">
          {/* Patient search */}
          <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Patient</label>
            {form.patient_id ? (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#205B5A]/20 bg-[#205B5A]/[0.03]"><span className="text-sm font-medium text-[#1A1B1A] flex-1">{search}</span><button type="button" onClick={() => { setForm(f => ({...f, patient_id: ""})); setSearch(""); }}><X className="w-3.5 h-3.5 text-[#757675]" /></button></div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" />
                <input value={search} onChange={e => setSearch(e.target.value)} data-testid="patient-search" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" placeholder="Search patient by name..." />
                {(patients.length > 0 || (search.length >= 2 && !searching)) && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {patients.map(p => (
                      <button type="button" key={p.id} onClick={() => { setForm(f => ({...f, patient_id: p.id})); setSearch(`${p.first_name} ${p.last_name}`); setPatients([]); }} className="w-full text-left px-3 py-2.5 hover:bg-[#FAF9F6] text-sm border-b border-[#1A1B1A]/[0.03] last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[8px] font-semibold text-[#205B5A]">{p.first_name?.[0]}{p.last_name?.[0]}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-[#1A1B1A]">{p.first_name} {p.last_name}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              {p.date_of_birth && <span className="text-[10px] text-[#757675]">DOB: {p.date_of_birth}</span>}
                              {p.phone && <span className="text-[10px] text-[#757675]">{p.phone}</span>}
                              {p.email && <span className="text-[10px] text-[#757675] truncate">{p.email}</span>}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                    {/* Always show create option when search has 2+ chars */}
                    {search.length >= 2 && !searching && (
                      <button type="button" onClick={() => { setShowNew(true); setPatients([]); setSearch(""); }} data-testid="create-new-patient-btn"
                        className="w-full text-left px-3 py-2.5 hover:bg-[#205B5A]/[0.03] text-sm text-[#205B5A] font-medium flex items-center gap-2 border-t border-[#1A1B1A]/[0.06]">
                        <div className="w-6 h-6 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0"><Plus className="w-3 h-3 text-[#205B5A]" /></div>
                        Create new patient
                        {patients.length > 0 && <span className="text-[10px] text-[#757675] font-normal ml-auto">Different person</span>}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {showNew && (
            <div className="bg-[#FAF9F6] rounded-xl p-3 space-y-2 border border-[#1A1B1A]/[0.06]">
              <p className="text-xs font-semibold text-[#1A1B1A]">New patient</p>
              <select value={newP.title} onChange={e => setNewP({...newP, title: e.target.value})} data-testid="new-patient-title" className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs">
                <option value="">Title (optional)</option>
                {["Mr", "Ms", "Mrs", "Miss", "Mx", "Dr", "Master", "Prefer not to say"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input value={newP.first_name} onChange={e => setNewP({...newP, first_name: e.target.value})} placeholder="First name" className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" data-testid="new-patient-first" />
                <input value={newP.last_name} onChange={e => setNewP({...newP, last_name: e.target.value})} placeholder="Last name" className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" data-testid="new-patient-last" />
              </div>
              <input type="date" value={newP.date_of_birth} onChange={e => setNewP({...newP, date_of_birth: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" data-testid="new-patient-dob" placeholder="Date of birth" />
              <div className="grid grid-cols-2 gap-2">
                <input value={newP.phone} onChange={e => setNewP({...newP, phone: e.target.value})} placeholder="Phone" className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" />
                <input value={newP.email} onChange={e => setNewP({...newP, email: e.target.value})} placeholder="Email" className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs" />
              </div>
              <div className="flex gap-2"><button type="button" onClick={createPat} className="text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-lg">Create</button><button type="button" onClick={() => setShowNew(false)} className="text-xs text-[#757675]">Cancel</button></div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Practitioner</label><select value={form.practitioner_id} onChange={e => setForm({...form, practitioner_id: e.target.value})} required data-testid="booking-practitioner" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">{bookable.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Type</label><select value={form.appointment_type_name} onChange={e => selType(e.target.value)} data-testid="booking-type" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">{apptTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}</select></div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Date</label><input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required data-testid="booking-date" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
            <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Start</label><input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} required data-testid="booking-start" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
            <div><label className="block text-xs font-medium text-[#757675] mb-1.5">Duration</label><select value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: Number(e.target.value)})} data-testid="booking-duration" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">{[15,20,30,45,60,75,90].map(m => <option key={m} value={m}>{m} min</option>)}</select></div>
          </div>

          <div className="flex items-center gap-4 px-3 py-2 bg-[#FAF9F6] rounded-xl"><div className="flex-1"><span className="text-[10px] text-[#757675] uppercase tracking-wider">End</span><p className="text-sm font-medium text-[#1A1B1A] font-mono">{form.end_time}</p></div><div><span className="text-[10px] text-[#757675] uppercase tracking-wider">Price</span><p className="text-sm font-semibold text-[#205B5A]">£{(form.price||0).toFixed(2)}</p></div></div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.recurring} onChange={e => setForm({...form, recurring: e.target.checked})} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-xs font-medium text-[#4A4B4A]">Recurring</span></label>
            {form.recurring && <div className="flex items-center gap-2"><select value={form.recurring_interval} onChange={e => setForm({...form, recurring_interval: e.target.value})} className="px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-xs"><option value="weekly">Weekly</option><option value="biweekly">Biweekly</option></select><input type="number" min={2} max={52} value={form.recurring_count} onChange={e => setForm({...form, recurring_count: Number(e.target.value)})} className="w-14 px-2 py-1 rounded-lg border border-[#1A1B1A]/10 text-xs text-center" /><span className="text-xs text-[#757675]">times</span></div>}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[#1A1B1A]/[0.06] mt-1">
            {onAddBlock ? <button type="button" onClick={() => { onClose(); onAddBlock(date); }} data-testid="booking-add-block-btn" className="text-[11px] font-medium text-[#5B6670] hover:text-[#205B5A] flex items-center gap-1.5 transition-colors"><Clock className="w-3.5 h-3.5" /> Add block time instead</button> : <div />}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm text-[#757675] hover:text-[#1A1B1A] transition-colors">Cancel</button>
              <button type="submit" disabled={!form.patient_id} data-testid="confirm-booking-btn"
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${form.patient_id ? "bg-[#205B5A] text-white hover:bg-[#174544] shadow-sm" : "bg-[#1A1B1A]/10 text-[#1A1B1A]/30 cursor-not-allowed"}`}>
                {form.recurring && form.recurring_count > 1 ? `Book ${form.recurring_count}` : "Book"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Device Form Fill (iPad/reception) ── */
function DeviceFormFill({ template, patientId, appointmentId, onComplete }) {
  const [responses, setResponses] = useState({});
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post("/forms/submissions", { template_id: template.id, patient_id: patientId, appointment_id: appointmentId, responses, signature, status: "completed" });
      setDone(true);
      setTimeout(onComplete, 1500);
    } catch {}
    setSubmitting(false);
  };

  if (done) return (
    <div className="flex-1 flex items-center justify-center"><div className="text-center"><CheckCircle2 className="w-12 h-12 text-[#205B5A] mx-auto mb-3" /><h3 className="text-lg font-medium text-[#1A1B1A]" style={H}>Form submitted</h3><p className="text-sm text-[#757675]">Thank you. Your form has been saved.</p></div></div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-xl mx-auto space-y-4">
        {template.fields?.map((f, i) => (
          <div key={f.id || i}>
            {f.field_type === "heading" && <h3 className="text-lg font-medium text-[#1A1B1A] mt-4 mb-1" style={H}>{f.label}</h3>}
            {f.field_type === "info_text" && <p className="text-sm text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">{f.label}</p>}
            {f.field_type === "short_text" && (<div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label><input value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>)}
            {f.field_type === "long_text" && (<div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label><textarea value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} rows={3} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" /></div>)}
            {f.field_type === "date" && (<div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label><input type="date" value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>)}
            {f.field_type === "checkbox" && (<label className="flex items-start gap-2.5 cursor-pointer"><input type="checkbox" checked={responses[f.id] || false} onChange={e => setResponses({...responses, [f.id]: e.target.checked})} className="rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5" /><span className="text-sm text-[#4A4B4A]">{f.label} {f.required && <span className="text-red-400">*</span>}</span></label>)}
            {f.field_type === "radio" && (<div><label className="text-xs font-medium text-[#1A1B1A] mb-2 block">{f.label}</label><div className="space-y-1.5">{f.options?.map((o, oi) => (<label key={oi} className="flex items-center gap-2 cursor-pointer"><input type="radio" name={f.id} value={o} checked={responses[f.id] === o} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="text-[#205B5A]" /><span className="text-sm text-[#4A4B4A]">{o}</span></label>))}</div></div>)}
            {f.field_type === "signature" && (<div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label><div className="border border-dashed border-[#1A1B1A]/15 rounded-xl p-4"><input value={signature} onChange={e => setSignature(e.target.value)} placeholder="Type your full name as signature" className="w-full text-lg italic text-[#1A1B1A] bg-transparent border-none outline-none" style={{ fontFamily: "cursive" }} data-testid="device-signature-input" /><p className="text-[10px] text-[#757675] mt-2">By typing your name, you provide your electronic signature.</p></div></div>)}
          </div>
        ))}
        <div className="pt-4 pb-8">
          <button onClick={handleSubmit} disabled={submitting} data-testid="device-submit-form-btn"
            className="w-full bg-[#205B5A] text-white py-3.5 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40">
            {submitting ? "Submitting..." : "Submit form"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Main Calendar ── */
export default function CalendarPage() {
  const { clinic } = useAuth();
  const [baseDate, setBaseDate] = useState(new Date());
  const [daysToShow, setDaysToShow] = useState(3);
  const [appointments, setAppointments] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPracs, setSelectedPracs] = useState(new Set());
  const [apptTypes, setApptTypes] = useState([]);
  const calNavigate = useNavigate();
  const [hideNames, setHideNames] = useState(false);
  const [showBooking, setShowBooking] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [showFormFill, setShowFormFill] = useState(null);
  const [openHour, setOpenHour] = useState(9);
  const [closeHour, setCloseHour] = useState(18);
  const [bookingInterval, setBookingInterval] = useState(5); // 5-min default for precision
  const [calBlocks, setCalBlocks] = useState([]);
  const [showBlockCreate, setShowBlockCreate] = useState(null);
  const todayStr = ds(new Date());
  
  // Ref to track if we just handled an appointment/block click (to prevent parent cell click)
  const justHandledAppointmentClick = useRef(false);

  // ═══════════════════════════════════════════════════════════════════════════
  // POINTER-BASED DRAG STATE (replacing HTML5 drag)
  // ═══════════════════════════════════════════════════════════════════════════
  const [dragState, setDragState] = useState(null);
  // dragState: { id, isBlock, appt, originalDate, originalTime, originalPracId, 
  //              duration_minutes, currentDate, currentTime, currentPracId, 
  //              offsetY, hasCollision, isDragging, startX, startY, originalColCenterX }
  const [showDragConfirm, setShowDragConfirm] = useState(null);
  // showDragConfirm: { id, isBlock, appt, fromDate, fromTime, toDate, toTime, toPracId }
  const calendarGridRef = useRef(null);
  const timeRowsRef = useRef(null); // Ref to the first time row for accurate positioning
  const hourHeight = 60; // pixels per hour
  
  // Drag behavior constants
  const DRAG_THRESHOLD = 5; // pixels before drag mode activates
  const COLUMN_SWITCH_THRESHOLD = 0.45; // Must cross 45% into neighbouring column to switch practitioner

  const dates = useMemo(() => Array.from({ length: daysToShow }, (_, i) => ad(baseDate, i)), [baseDate, daysToShow]);
  const startStr = ds(dates[0]), endStr = ds(dates[dates.length - 1]);

  const loadData = useCallback(async () => {
    const [a, t, at, w, s, bl] = await Promise.all([
      api.get(`/appointments?start_date=${startStr}&end_date=${endStr}&limit=500`),
      api.get("/practitioners?include_inactive=true"), api.get("/appointment-types"), api.get("/waitlist"),
      api.get("/settings/preferences").catch(() => ({ data: {} })),
      api.get(`/blocks?start_date=${startStr}&end_date=${endStr}`).catch(() => ({ data: { blocks: [] } })),
    ]);
    setAppointments(a.data.appointments);
    const pracs = t.data.practitioners || [];
    setPractitioners(pracs);
    if (selectedPracs.size === 0) setSelectedPracs(new Set(pracs.filter(p => p.is_active_practitioner).map(p => p.id)));
    setApptTypes(at.data.appointment_types);
    setWaitlistCount(w.data.total);
    if (s.data.working_hours_start) setOpenHour(parseInt(s.data.working_hours_start.split(":")[0], 10));
    if (s.data.working_hours_end) setCloseHour(parseInt(s.data.working_hours_end.split(":")[0], 10));
    if (s.data.booking_interval) setBookingInterval(s.data.booking_interval);
    setCalBlocks(bl.data.blocks || []);
  }, [startStr, endStr]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredAppts = useMemo(() => appointments.filter(a => selectedPracs.has(a.practitioner_id) && a.status !== "cancelled"), [appointments, selectedPracs]);
  const apptsByDate = useMemo(() => { const m = {}; filteredAppts.forEach(a => { if (!m[a.date]) m[a.date] = []; m[a.date].push(a); }); return m; }, [filteredAppts]);
  const blocksByDate = useMemo(() => { const m = {}; calBlocks.forEach(b => { if (!m[b.date]) m[b.date] = []; m[b.date].push(b); }); return m; }, [calBlocks]);

  // Auto-assign colours to appointment types, respecting custom overrides
  const typeColorMap = useMemo(() => {
    const m = {};
    apptTypes.forEach((t, i) => {
      const palette = TYPE_PALETTE[i % TYPE_PALETTE.length];
      if (t.color && !["#205B5A", "#496275", ""].includes(t.color)) {
        // Custom colour — generate a solid light bg from it
        const hex = t.color.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const bgR = Math.round(r + (255 - r) * 0.78);
        const bgG = Math.round(g + (255 - g) * 0.78);
        const bgB = Math.round(b + (255 - b) * 0.78);
        m[t.name] = { bg: `#${bgR.toString(16).padStart(2,"0")}${bgG.toString(16).padStart(2,"0")}${bgB.toString(16).padStart(2,"0")}`, border: t.color, text: t.color };
      } else {
        m[t.name] = palette;
      }
    });
    return m;
  }, [apptTypes]);

  const getTypeColor = (typeName) => typeColorMap[typeName] || TYPE_PALETTE[0];

  // Visible practitioners (filtered by selection)
  const visPracs = useMemo(() => practitioners.filter(p => selectedPracs.has(p.id)), [practitioners, selectedPracs]);

  const togglePrac = (id) => { setSelectedPracs(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const handleOpenNote = (appt, note) => {
    setSelectedAppt(null);
    calNavigate(`/app/patients/${appt.patient_id}?tab=notes`);
  };

  // Calculate snapped time from mouse Y position relative to the cell
  const calcSnapTime = (e, targetHour) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const yRatio = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const minuteInHour = Math.floor(yRatio * 60);
    const snapped = Math.round(minuteInHour / bookingInterval) * bookingInterval;
    const clampedMin = Math.min(snapped, 60 - bookingInterval);
    const totalMin = targetHour * 60 + clampedMin;
    return `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // POINTER-BASED DRAG SYSTEM (Treatwell-level smoothness)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Convert pixel position to time with magnetic snapping
  const pixelToTime = useCallback((pixelY, containerTop) => {
    const relativeY = pixelY - containerTop;
    const totalMinutes = openHour * 60 + (relativeY / hourHeight) * 60;
    // Magnetic snap to interval
    const snapped = Math.round(totalMinutes / bookingInterval) * bookingInterval;
    // Clamp to working hours
    const clamped = Math.max(openHour * 60, Math.min(closeHour * 60 - bookingInterval, snapped));
    return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
  }, [openHour, closeHour, bookingInterval, hourHeight]);

  // Convert time to pixel position
  const timeToPixel = useCallback((timeStr) => {
    const mins = tm(timeStr);
    return ((mins - openHour * 60) / 60) * hourHeight;
  }, [openHour, hourHeight]);

  // Check for collision with other appointments
  const checkCollision = useCallback((targetDate, startTime, duration, excludeId, pracId) => {
    const startMin = tm(startTime);
    const endMin = startMin + duration;
    
    // Check appointments
    const dayAppts = (apptsByDate[targetDate] || []).filter(a => 
      a.id !== excludeId && 
      a.practitioner_id === pracId &&
      a.status !== "cancelled"
    );
    
    for (const a of dayAppts) {
      const aStart = tm(a.start_time);
      const aEnd = tm(a.end_time);
      if (startMin < aEnd && endMin > aStart) {
        return true; // Collision!
      }
    }
    
    // Check blocks
    const dayBlocks = (blocksByDate[targetDate] || []).filter(b => 
      !b.practitioner_id || b.practitioner_id === pracId
    );
    
    for (const b of dayBlocks) {
      const bStart = tm(b.start_time);
      const bEnd = tm(b.end_time);
      if (startMin < bEnd && endMin > bStart) {
        return true; // Collision!
      }
    }
    
    return false;
  }, [apptsByDate, blocksByDate]);

  // Start dragging an appointment - but don't enter drag mode until threshold crossed
  const handleDragStart = useCallback((e, appt, isBlock = false) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate offset from top of card to click point
    const cardRect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - cardRect.top;
    
    const duration = isBlock 
      ? tm(appt.end_time) - tm(appt.start_time)
      : appt.duration_minutes || 30;
    
    // Calculate the center X position of the original practitioner column
    // This will be used to determine if user has intentionally moved to another column
    let originalColCenterX = e.clientX;
    if (calendarGridRef.current && visPracs.length > 0) {
      const gridRect = calendarGridRef.current.getBoundingClientRect();
      const colWidth = gridRect.width / (dates.length * visPracs.length);
      
      // Find the original practitioner's column index
      const originalPracIndex = visPracs.findIndex(p => p.id === appt.practitioner_id);
      const dateIndex = dates.findIndex(d => ds(d) === appt.date);
      if (originalPracIndex >= 0 && dateIndex >= 0) {
        const colIndex = dateIndex * visPracs.length + originalPracIndex;
        originalColCenterX = gridRect.left + (colIndex + 0.5) * colWidth;
      }
    }
    
    setDragState({
      id: appt.id,
      isBlock,
      appt,
      originalDate: appt.date,
      originalTime: appt.start_time,
      originalPracId: appt.practitioner_id,
      duration_minutes: duration,
      currentDate: appt.date,
      currentTime: appt.start_time,
      currentPracId: appt.practitioner_id,
      offsetY,
      hasCollision: false,
      pointerX: e.clientX,
      pointerY: e.clientY,
      // New: track if we're actually dragging (past threshold) vs just clicking
      isDragging: false,
      startX: e.clientX,
      startY: e.clientY,
      originalColCenterX,
      // GHOST: Capture the exact bounding rect of the original card for pixel-perfect ghost
      ghostRect: {
        top: cardRect.top,
        left: cardRect.left,
        width: cardRect.width,
        height: cardRect.height,
      },
    });
    
    // Capture pointer for smooth tracking
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [visPracs, dates]);

  // Handle pointer move during drag
  const handleDragMove = useCallback((e) => {
    if (!dragState || !calendarGridRef.current || !timeRowsRef.current) return;
    
    // Check if we've crossed the drag threshold
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // If not yet in drag mode, check if threshold crossed
    if (!dragState.isDragging) {
      if (distance < DRAG_THRESHOLD) {
        // Haven't moved enough - don't update anything
        return;
      }
      // Threshold crossed - enter drag mode
      setDragState(prev => ({ ...prev, isDragging: true }));
    }
    
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      if (!calendarGridRef.current || !timeRowsRef.current) return;
      
      const gridRect = calendarGridRef.current.getBoundingClientRect();
      const timeRowsRect = timeRowsRef.current.getBoundingClientRect();
      
      // IMPORTANT: Account for the time-axis gutter (56px = w-14) on the left
      const TIME_GUTTER_WIDTH = 56;
      const columnsWidth = gridRect.width - TIME_GUTTER_WIDTH;
      const columnsLeft = gridRect.left + TIME_GUTTER_WIDTH;
      
      // Calculate new time from Y position
      const adjustedY = e.clientY - dragState.offsetY;
      const pixelsFromTimeRowsTop = adjustedY - timeRowsRect.top;
      const minutesFromFirstHour = (pixelsFromTimeRowsTop / hourHeight) * 60;
      const totalMinutes = HOURS[0] * 60 + minutesFromFirstHour;
      
      // Magnetic snap to interval
      const snapped = Math.round(totalMinutes / bookingInterval) * bookingInterval;
      // Clamp to working hours
      const clamped = Math.max(openHour * 60, Math.min(closeHour * 60 - bookingInterval, snapped));
      const newTime = `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
      
      // ═══════════════════════════════════════════════════════════════════════
      // PRACTITIONER COLUMN LOGIC - Only switch if clearly moved into new column
      // ═══════════════════════════════════════════════════════════════════════
      const totalCols = dates.length * visPracs.length;
      const colWidth = columnsWidth / totalCols;
      const relativeX = e.clientX - columnsLeft;  // Relative to columns area, not including gutter
      
      // Calculate which column the pointer is currently over
      const currentColIndex = Math.max(0, Math.min(totalCols - 1, Math.floor(relativeX / colWidth)));
      const dateIndex = Math.floor(currentColIndex / visPracs.length);
      const pracIndexOverColumn = currentColIndex % visPracs.length;
      
      // Find the original practitioner's column index
      const originalPracIndex = visPracs.findIndex(p => p.id === dragState.originalPracId);
      const originalDateIndex = dates.findIndex(d => ds(d) === dragState.originalDate);
      const originalColIndex = originalDateIndex >= 0 && originalPracIndex >= 0 
        ? originalDateIndex * visPracs.length + originalPracIndex 
        : currentColIndex;
      
      // Calculate position within the current column (0 = left edge, 1 = right edge)
      const positionInColumn = (relativeX - currentColIndex * colWidth) / colWidth;
      
      // Determine the target practitioner based on intentional horizontal movement
      let targetPracId = dragState.originalPracId;
      let targetDate = dragState.originalDate;
      
      // Only change practitioner if:
      // 1. The pointer is in a different column than the original
      // 2. The pointer has crossed COLUMN_SWITCH_THRESHOLD into that column
      if (currentColIndex !== originalColIndex) {
        const isMovingLeft = currentColIndex < originalColIndex;
        const isMovingRight = currentColIndex > originalColIndex;
        
        // For moving left: need to be past (1 - threshold) from left edge (i.e., into left part of column)
        // For moving right: need to be past threshold from left edge (i.e., into right part of column)
        const shouldSwitch = isMovingLeft 
          ? positionInColumn < (1 - COLUMN_SWITCH_THRESHOLD)  // Must be in left 55% of new column
          : positionInColumn > COLUMN_SWITCH_THRESHOLD;       // Must be in right 55% of new column
        
        if (shouldSwitch) {
          targetPracId = visPracs[Math.max(0, Math.min(pracIndexOverColumn, visPracs.length - 1))]?.id || dragState.originalPracId;
          targetDate = dates[Math.max(0, Math.min(dateIndex, dates.length - 1))]?.toISOString().split("T")[0] || dragState.originalDate;
        }
      }
      
      // ═══════════════════════════════════════════════════════════════════════
      // CALCULATE TARGET COLUMN POSITION FOR PREVIEW
      // The preview must snap to the target column, NOT follow raw pointer X
      // ═══════════════════════════════════════════════════════════════════════
      
      // Find the target column's visual position
      const targetPracIndex = visPracs.findIndex(p => p.id === targetPracId);
      const targetDateIndex = dates.findIndex(d => ds(d) === targetDate);
      const targetColIndex = targetDateIndex >= 0 && targetPracIndex >= 0
        ? targetDateIndex * visPracs.length + targetPracIndex
        : originalColIndex;
      
      // Calculate the pixel bounds of the target column (accounting for time gutter)
      const targetColLeft = columnsLeft + targetColIndex * colWidth;
      const targetColWidth = colWidth;
      
      // Calculate the pixel Y position for the snapped time
      const snappedMinutes = clamped;
      const pixelTop = timeRowsRect.top + ((snappedMinutes - HOURS[0] * 60) / 60) * hourHeight;
      const pixelHeight = (dragState.duration_minutes / 60) * hourHeight;
      
      // Check for collisions with the target practitioner
      const hasCollision = checkCollision(
        targetDate, 
        newTime, 
        dragState.duration_minutes, 
        dragState.id,
        targetPracId
      );
      
      // Check boundary (working hours)
      const newTimeMin = tm(newTime);
      const endMin = newTimeMin + dragState.duration_minutes;
      const outOfBounds = newTimeMin < openHour * 60 || endMin > closeHour * 60;
      
      setDragState(prev => ({
        ...prev,
        currentDate: targetDate,
        currentTime: newTime,
        currentPracId: targetPracId,
        hasCollision: hasCollision || outOfBounds,
        pointerX: e.clientX,
        pointerY: e.clientY,
        isDragging: true,
        // PREVIEW POSITION: Snap to target column, not raw pointer
        previewRect: {
          left: targetColLeft + 2,  // +2 for left-0.5 padding match
          top: pixelTop,
          width: targetColWidth - 4, // -4 for left/right padding match
          height: Math.max(24, pixelHeight),
        },
      }));
    });
  }, [dragState, dates, visPracs, checkCollision, openHour, closeHour, bookingInterval]);

  // End drag - show confirmation modal OR handle as click
  const handleDragEnd = useCallback((e) => {
    if (!dragState) return;
    
    // If never entered drag mode (didn't cross threshold), treat as a click
    if (!dragState.isDragging) {
      const apptId = dragState.id;
      const isBlock = dragState.isBlock;
      setDragState(null);
      
      // Open the appointment drawer on clean click (not for blocks)
      if (!isBlock) {
        // IMPORTANT: Clear booking modal state to prevent it from opening
        setShowBooking(null);
        setSelectedAppt(apptId);
      }
      return;
    }
    
    // Check if position actually changed
    const hasTimeChanged = dragState.originalTime !== dragState.currentTime;
    const hasDateChanged = dragState.originalDate !== dragState.currentDate;
    const hasPracChanged = dragState.originalPracId !== dragState.currentPracId;
    const hasChanged = hasTimeChanged || hasDateChanged || hasPracChanged;
    
    if (!hasChanged || dragState.hasCollision) {
      // No change or collision - cancel
      setDragState(null);
      return;
    }
    
    // Show confirmation modal
    setShowDragConfirm({
      id: dragState.id,
      isBlock: dragState.isBlock,
      appt: dragState.appt,
      fromDate: dragState.originalDate,
      fromTime: dragState.originalTime,
      fromPracId: dragState.originalPracId,
      toDate: dragState.currentDate,
      toTime: dragState.currentTime,
      toPracId: dragState.currentPracId,
      duration: dragState.duration_minutes,
      // Track what actually changed for the modal display
      practitionerChanged: hasPracChanged,
    });
    
    setDragState(null);
  }, [dragState]);

  // Confirm drag change
  const confirmDragChange = async () => {
    if (!showDragConfirm) return;
    
    const { id, isBlock, toDate, toTime, toPracId, duration } = showDragConfirm;
    const startMin = tm(toTime);
    const endMin = startMin + duration;
    const newEnd = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    
    try {
      if (isBlock) {
        await api.put(`/blocks/${id}`, { 
          date: toDate, 
          start_time: toTime, 
          end_time: newEnd,
          practitioner_id: toPracId || null,
        });
      } else {
        await api.put(`/appointments/${id}`, { 
          date: toDate, 
          start_time: toTime, 
          end_time: newEnd,
          practitioner_id: toPracId,
        });
      }
      loadData();
    } catch (err) {
      console.error("Failed to update:", err);
    }
    
    setShowDragConfirm(null);
  };

  // Cancel drag change
  const cancelDragChange = () => {
    setShowDragConfirm(null);
  };

  // Calculate end time for display
  const calcEndTimeStr = (startTime, duration) => {
    const startMin = tm(startTime);
    const endMin = startMin + duration;
    return `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
  };

  // Global pointer move handler
  useEffect(() => {
    if (!dragState) return;
    
    const handleMove = (e) => handleDragMove(e);
    const handleUp = (e) => handleDragEnd(e);
    
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [dragState, handleDragMove, handleDragEnd]);

  // OLD handleDrop kept for blocks (will be migrated)
  const handleDrop = async (e, targetDate, targetHour) => {
    // This is now mostly unused - pointer system handles it
    return;
  };

  // Block resize handler
  const handleBlockResize = async (blockId, edge, e, targetHour) => {
    const newTime = calcSnapTime(e, targetHour);
    const block = calBlocks.find(b => b.id === blockId);
    if (!block) return;
    if (edge === "bottom") {
      if (tm(newTime) > tm(block.start_time)) {
        await api.put(`/blocks/${blockId}`, { end_time: newTime });
        loadData();
      }
    } else if (edge === "top") {
      if (tm(newTime) < tm(block.end_time)) {
        await api.put(`/blocks/${blockId}`, { start_time: newTime });
        loadData();
      }
    }
  };

  const handleBlockSave = async (blockData) => {
    await api.post("/blocks", blockData);
    setShowBlockCreate(null);
    loadData();
  };
  const handleCollectForms = async (appt) => {
    // Load pending form templates for this appointment
    if (!appt.form_assignments) return;
    const pending = appt.form_assignments.filter(fa => !fa.submission);
    if (pending.length === 0) return;
    // Load the first pending form template
    try {
      const { data } = await api.get(`/forms/templates/${pending[0].template_id}`);
      setShowFormFill({ template: data, patientId: appt.patient_id, appointmentId: appt.id });
      setSelectedAppt(null);
    } catch {}
  };

  return (
    <div className="flex h-full" data-testid="calendar-page">
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1A1B1A]/[0.06] bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-medium text-[#1A1B1A]" style={H}>Calendar</h1>
            <div className="flex items-center bg-[#FAF9F6] rounded-lg p-0.5 gap-0.5">{[1,3,7].map(d => (
              <button key={d} onClick={() => setDaysToShow(d)} data-testid={`view-${d}-day`} className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${daysToShow === d ? "bg-white shadow-sm text-[#1A1B1A]" : "text-[#757675]"}`}>{d === 1 ? "Day" : d === 3 ? "3 Day" : "Week"}</button>
            ))}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setBaseDate(d => ad(d, -daysToShow))} className="p-1.5 hover:bg-[#FAF9F6] rounded-lg" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-[#4A4B4A]" /></button>
            <button onClick={() => setBaseDate(new Date())} data-testid="cal-today-btn" className="text-[11px] font-medium text-[#205B5A] px-3 py-1 rounded-full border border-[#205B5A]/20 hover:bg-[#205B5A]/5">Today</button>
            <button onClick={() => setBaseDate(d => ad(d, daysToShow))} className="p-1.5 hover:bg-[#FAF9F6] rounded-lg" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-[#4A4B4A]" /></button>
            <button onClick={() => setShowBooking({ date: todayStr, time: "09:00" })} data-testid="cal-new-appointment-btn" className="bg-[#205B5A] text-white px-3.5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1.5 ml-2"><Plus className="w-3.5 h-3.5" /> Book</button>
            <button onClick={() => setShowBlockCreate({ date: todayStr, start_time: "12:00", end_time: "13:00" })} data-testid="cal-add-block-btn" className="border border-[#1A1B1A]/15 text-[#4A4B4A] px-3 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#FAF9F6] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Block</button>
          </div>
        </div>

        {/* Time grid - practitioner columns under each date */}
        <div className="flex-1 overflow-auto" ref={calendarGridRef}>
          <div style={{ minWidth: Math.max(500, dates.length * visPracs.length * 120 + 56) }}>
            {/* Day + Practitioner headers */}
            <div className="flex border-b-2 border-[#D6E2DD] bg-white sticky top-0 z-20">
              <div className="w-14 flex-shrink-0 border-r border-[#D6E2DD]" />
              {dates.map(d => { const s = ds(d), isT = s === todayStr; return (
                <div key={s} className="flex-1 border-l border-[#D6E2DD]">
                  {/* Date header */}
                  <div className={`px-2 py-1.5 text-center border-b border-[#D6E2DD] ${isT ? "bg-[#DCEFE8]" : "bg-[#F8FAF9]"}`}>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${isT ? "text-[#123C4A]" : "text-[#6B7280]"}`}>{d.toLocaleDateString("en-GB", { weekday: "short" })} </span>
                    <span className={`text-sm font-semibold ${isT ? "text-[#123C4A]" : "text-[#1A1B1A]"}`} style={H}>{d.getDate()}</span>
                  </div>
                  {/* Practitioner sub-headers */}
                  <div className="flex">
                    {visPracs.map((pr, pi) => (
                      <div key={pr.id} className={`flex-1 px-1.5 py-1 text-center border-l border-[#E9F0ED] first:border-l-0 ${isT ? "bg-[#F0F8F5]" : "bg-white"}`}>
                        <p className="text-[9px] font-semibold text-[#5B6670] truncate">{pr.full_name?.split(" ")[0]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ); })}
            </div>

            {/* Time rows */}
            <div ref={timeRowsRef}>
            {HOURS.map(hour => (
              <div key={hour} className="flex relative" style={{ minHeight: hourHeight }}>
                <div className="w-14 flex-shrink-0 text-right pr-2.5 border-r border-[#D6E2DD] relative">
                  <span className="text-[10px] font-mono text-[#5B6670] absolute -top-1 right-2.5">{String(hour).padStart(2,"0")}:00</span>
                </div>
                {dates.map(d => { const dateStr = ds(d), isT = dateStr === todayStr;
                  return (
                    <div key={dateStr} className="flex-1 flex border-l border-[#D6E2DD]">
                      {visPracs.map((pr) => {
                        const isClosed = hour < openHour || hour >= closeHour;
                        const pracAppts = (apptsByDate[dateStr] || []).filter(a => parseInt(a.start_time?.split(":")[0], 10) === hour && a.practitioner_id === pr.id);
                        const pracBlocks = (blocksByDate[dateStr] || []).filter(b => parseInt(b.start_time?.split(":")[0], 10) === hour && (b.practitioner_id === pr.id || !b.practitioner_id));

                        // Only highlight the target column when actually dragging (past threshold)
                        // and only for the CURRENT target practitioner (not neighbours)
                        const isDragTarget = dragState?.isDragging && 
                          dragState.currentDate === dateStr && 
                          dragState.currentPracId === pr.id && 
                          Math.floor(tm(dragState.currentTime) / 60) === hour;

                        return (
                          <div key={pr.id}
                            className={`flex-1 border-l border-t first:border-l-0 relative transition-colors ${
                              isClosed ? "bg-[#EEF3F1]" : 
                              isDragTarget ? (dragState.hasCollision ? "bg-red-50" : "bg-[#D4EDEC]") :
                              isT ? "bg-[#FBFDFC] cursor-pointer hover:bg-[#EFF8F4]" : 
                              "bg-white cursor-pointer hover:bg-[#F7FAF9]"
                            }`}
                            style={{ borderTopColor: "#CDD8D3", borderLeftColor: "#D9E3DE", height: hourHeight }}
                            onClick={() => {
                              // Only open booking modal if:
                              // 1. Cell is not closed
                              // 2. No drag is in progress  
                              // 3. We didn't just handle an appointment/block click (prevents event bubbling issues)
                              if (!isClosed && !dragState && !justHandledAppointmentClick.current) {
                                setShowBooking({ date: dateStr, time: `${String(hour).padStart(2,"0")}:00` });
                              }
                            }}>
                            {/* 15-min lines */}
                            <div className="absolute left-0 right-0 top-[25%] border-t border-[#E9F0ED]" />
                            <div className="absolute left-0 right-0 top-[50%] border-t border-[#E9F0ED]" style={{ borderTopStyle: "dashed" }} />
                            <div className="absolute left-0 right-0 top-[75%] border-t border-[#E9F0ED]" />

                            {/* Appointments - coloured by type with pointer-based drag */}
                            {pracAppts.map(a => {
                              const tc = getTypeColor(a.appointment_type_name);
                              const sM = tm(a.start_time) - hour * 60;
                              const dur = a.duration_minutes || 30;
                              const isDragging = dragState?.id === a.id && dragState?.isDragging;
                              
                              // When dragging, hide the original card completely (ghost is rendered separately)
                              if (isDragging) return null;
                              
                              return (
                                <div key={a.id}
                                  onPointerDown={e => handleDragStart(e, a, false)}
                                  onClick={e => {
                                    // CRITICAL: Stop click event from bubbling to parent cell
                                    // This prevents the booking modal from opening when clicking an appointment
                                    e.stopPropagation();
                                    // Also set the ref to guard against any race conditions
                                    justHandledAppointmentClick.current = true;
                                    setTimeout(() => { justHandledAppointmentClick.current = false; }, 100);
                                  }}
                                  data-testid={`appt-card-${a.id}`}
                                  className="absolute left-0.5 right-0.5 rounded-md border-l-[4px] px-2 py-1 overflow-hidden cursor-grab active:cursor-grabbing hover:shadow-lg shadow-sm select-none touch-none"
                                  style={{ 
                                    top: Math.max(0, (sM/60)*hourHeight), 
                                    height: Math.max(24, (dur/60)*hourHeight), 
                                    minHeight: 24, 
                                    zIndex: 10, 
                                    backgroundColor: tc.bg, 
                                    borderLeftColor: tc.border, 
                                    borderTop: `1px solid ${tc.border}25`, 
                                    borderRight: `1px solid ${tc.border}25`, 
                                    borderBottom: `1px solid ${tc.border}25`, 
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)" 
                                  }}>
                                  <p className="text-[9px] font-bold truncate leading-tight" style={{ color: tc.text }}>{hideNames ? "Patient" : a.patient_name}</p>
                                  <p className="text-[8px] truncate leading-tight mt-px font-semibold" style={{ color: tc.text, opacity: 0.65 }}>
                                    {a.start_time} · {a.appointment_type_name}
                                  </p>
                                </div>
                              );
                            })}

                            {/* Blocks with pointer-based drag */}
                            {pracBlocks.map(b => {
                              const bsM = tm(b.start_time) - hour * 60;
                              const bDur = tm(b.end_time) - tm(b.start_time);
                              const isDragging = dragState?.id === b.id && dragState?.isDragging;
                              
                              // When dragging, hide the original block (ghost is rendered separately)
                              if (isDragging) return null;
                              
                              return (
                                <div key={b.id} 
                                  data-testid={`block-${b.id}`}
                                  onPointerDown={e => handleDragStart(e, b, true)}
                                  onClick={e => {
                                    // CRITICAL: Stop click event from bubbling to parent cell
                                    // This prevents the booking modal from opening when clicking a block
                                    e.stopPropagation();
                                    // Also set the ref to guard against any race conditions
                                    justHandledAppointmentClick.current = true;
                                    setTimeout(() => { justHandledAppointmentClick.current = false; }, 100);
                                  }}
                                  className="absolute left-0.5 right-0.5 rounded-md px-1.5 py-0.5 overflow-hidden border border-dashed border-[#B0BEC5]/50 cursor-grab active:cursor-grabbing group select-none touch-none"
                                  style={{ 
                                    top: Math.max(0, (bsM/60)*hourHeight), 
                                    height: Math.max(16, (bDur/60)*hourHeight), 
                                    minHeight: 16, 
                                    zIndex: 9, 
                                    backgroundColor: "#DDE3E6", 
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)" 
                                  }}>
                                  <span className="text-[8px] text-[#5B6670] truncate block font-semibold">
                                    {b.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
            </div>
          </div>
        </div>

        {/* GHOST PLACEHOLDER - Fixed position using captured bounding rect for pixel-perfect alignment */}
        {dragState?.isDragging && dragState?.ghostRect && (
          <div 
            className="fixed pointer-events-none z-40"
            data-testid="drag-ghost"
            style={{
              top: dragState.ghostRect.top,
              left: dragState.ghostRect.left,
              width: dragState.ghostRect.width,
              height: dragState.ghostRect.height,
            }}
          >
            <div 
              className={`w-full h-full rounded-md border-l-[4px] px-2 py-1 overflow-hidden ${
                dragState.isBlock ? "border border-dashed border-[#B0BEC5]/30" : ""
              }`}
              style={{ 
                backgroundColor: dragState.isBlock ? "#DDE3E6" : getTypeColor(dragState.appt.appointment_type_name).bg,
                borderLeftColor: dragState.isBlock ? "#78909C" : getTypeColor(dragState.appt.appointment_type_name).border,
                opacity: 0.4,
              }}
            >
              <p className="text-[9px] font-bold truncate leading-tight" style={{ color: dragState.isBlock ? "#5B6670" : getTypeColor(dragState.appt.appointment_type_name).text }}>
                {dragState.isBlock ? dragState.appt.label : (hideNames ? "Patient" : dragState.appt.patient_name)}
              </p>
              <p className="text-[8px] truncate leading-tight mt-px font-semibold" style={{ color: dragState.isBlock ? "#5B6670" : getTypeColor(dragState.appt.appointment_type_name).text, opacity: 0.65 }}>
                {dragState.originalTime} · {dragState.isBlock ? "Block" : dragState.appt.appointment_type_name}
              </p>
            </div>
          </div>
        )}

        {/* Floating drag preview card - SNAPS TO TARGET COLUMN, not raw pointer */}
        {dragState?.isDragging && dragState?.previewRect && (
          <div 
            className="fixed pointer-events-none z-50"
            data-testid="drag-preview"
            style={{
              left: dragState.previewRect.left,
              top: dragState.previewRect.top,
              width: dragState.previewRect.width,
              height: dragState.previewRect.height,
              transform: "scale(1.02)",
              transformOrigin: "top left",
            }}
          >
            <div 
              className={`w-full h-full rounded-md shadow-2xl border-l-[4px] px-2 py-1 overflow-hidden ${
                dragState.hasCollision ? "ring-2 ring-red-400" : "ring-2 ring-[#205B5A]"
              }`}
              style={{ 
                backgroundColor: dragState.isBlock ? "#DDE3E6" : getTypeColor(dragState.appt.appointment_type_name).bg,
                borderLeftColor: dragState.isBlock ? "#78909C" : getTypeColor(dragState.appt.appointment_type_name).border,
              }}
            >
              {/* Live time indicator */}
              <div className={`text-[10px] font-bold mb-0.5 ${dragState.hasCollision ? "text-red-600" : "text-[#205B5A]"}`}>
                {dragState.currentTime} – {calcEndTimeStr(dragState.currentTime, dragState.duration_minutes)}
              </div>
              <p className="text-[9px] font-semibold truncate" style={{ color: dragState.isBlock ? "#5B6670" : getTypeColor(dragState.appt.appointment_type_name).text }}>
                {dragState.isBlock ? dragState.appt.label : (hideNames ? "Patient" : dragState.appt.patient_name)}
              </p>
              {dragState.hasCollision && (
                <p className="text-[8px] text-red-500 font-medium mt-0.5">Conflict!</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Drag target time shown inside card — no floating tooltip */}


      {/* Tools Panel */}
      <div className="w-[220px] border-l border-[#1A1B1A]/[0.06] bg-white flex-shrink-0 overflow-y-auto hidden lg:block" data-testid="calendar-tools">
        <div className="p-4 space-y-5">
          <MiniCal selected={ds(baseDate)} onSelect={(d) => setBaseDate(new Date(d))} />
          <div><p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2">Practitioners</p>{practitioners.map(p => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#FAF9F6] cursor-pointer"><input type="checkbox" checked={selectedPracs.has(p.id)} onChange={() => togglePrac(p.id)} className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-3.5 h-3.5" /><span className="text-xs font-medium text-[#4A4B4A] truncate">{p.full_name}</span>{p.is_active_practitioner === false && <span className="text-[9px] text-[#9A9B9A] ml-auto flex-shrink-0">former</span>}</label>
          ))}</div>
          <div><p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2">Wait list</p><Link to="/app/appointments" className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#FAF9F6] hover:bg-[#EBEAE4] text-xs font-medium text-[#4A4B4A]">View list <span className="text-[10px] font-semibold text-[#205B5A] bg-[#205B5A]/10 px-1.5 py-0.5 rounded-full">{waitlistCount}</span></Link></div>
          <div><p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-2">Privacy</p><label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#FAF9F6] cursor-pointer" data-testid="hide-names-toggle"><input type="checkbox" checked={hideNames} onChange={e => setHideNames(e.target.checked)} className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-3.5 h-3.5" /><span className="text-xs font-medium text-[#4A4B4A]">Hide names</span>{hideNames ? <EyeOff className="w-3 h-3 text-[#757675] ml-auto" /> : <Eye className="w-3 h-3 text-[#757675] ml-auto" />}</label></div>
        </div>
      </div>

      {/* Appointment Drawer */}
      {selectedAppt && <ApptDrawer apptId={selectedAppt} onClose={() => setSelectedAppt(null)} onUpdated={loadData} onOpenNote={handleOpenNote} onCollectForms={handleCollectForms} onBookAnother={(appt) => { setSelectedAppt(null); setShowBooking({ date: todayStr, time: "09:00", prefillPatientId: appt.patient_id, prefillPatientName: `${appt.patient?.first_name || ""} ${appt.patient?.last_name || ""}`.trim() }); }} apptTypes={apptTypes} practitioners={practitioners} clinicName={clinic?.name || ""} />}

      {/* Booking Modal */}
      {showBooking && <BookingModal date={showBooking.date} time={showBooking.time} practitioners={practitioners} apptTypes={apptTypes} onClose={() => setShowBooking(null)} onCreated={() => { setShowBooking(null); loadData(); }} prefillPatientId={showBooking.prefillPatientId} prefillPatientName={showBooking.prefillPatientName} onAddBlock={(d) => setShowBlockCreate({ date: d || todayStr, start_time: "12:00", end_time: "13:00", label: "", block_type: "break" })} />}

      {/* Note Editor */}
      {showNoteEditor && <NoteEditor appt={showNoteEditor.appt} existingNote={showNoteEditor.note} onClose={() => setShowNoteEditor(null)} onSaved={() => { setShowNoteEditor(null); loadData(); }} />}

      {/* Form Fill on device */}
      {showFormFill && (
        <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="device-form-fill">
          <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowFormFill(null)} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
              <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>{showFormFill.template.name}</h2>
            </div>
            <span className="text-[10px] font-medium text-[#205B5A] bg-[#205B5A]/10 px-3 py-1 rounded-full">Patient signing on device</span>
          </div>
          <DeviceFormFill template={showFormFill.template} patientId={showFormFill.patientId} appointmentId={showFormFill.appointmentId}
            onComplete={() => { setShowFormFill(null); loadData(); }} />
        </div>
      )}

      {/* Block Creation Modal */}
      {showBlockCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="block-create-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Block time</h2>
            <form onSubmit={e => { e.preventDefault(); handleBlockSave(showBlockCreate); }} className="space-y-3">
              <input value={showBlockCreate.label || ""} onChange={e => setShowBlockCreate({...showBlockCreate, label: e.target.value})} required placeholder="Label (e.g. Lunch, Break, Admin)" data-testid="block-label"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <select value={showBlockCreate.block_type || "break"} onChange={e => setShowBlockCreate({...showBlockCreate, block_type: e.target.value})} data-testid="block-type"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm">
                {["break", "lunch", "admin", "meeting", "travel", "unavailable", "custom"].map(t => <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-[10px] text-[#757675] mb-1 block">Date</label><input type="date" value={showBlockCreate.date} onChange={e => setShowBlockCreate({...showBlockCreate, date: e.target.value})} required className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm" /></div>
                <div><label className="text-[10px] text-[#757675] mb-1 block">Start</label><input type="time" value={showBlockCreate.start_time} onChange={e => setShowBlockCreate({...showBlockCreate, start_time: e.target.value})} required className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm" /></div>
                <div><label className="text-[10px] text-[#757675] mb-1 block">End</label><input type="time" value={showBlockCreate.end_time} onChange={e => setShowBlockCreate({...showBlockCreate, end_time: e.target.value})} required className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm" /></div>
              </div>
              <select value={showBlockCreate.practitioner_id || ""} onChange={e => setShowBlockCreate({...showBlockCreate, practitioner_id: e.target.value})}
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm">
                <option value="">All practitioners</option>
                {practitioners.filter(p => p.is_active_practitioner !== false).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
              <div>
                <label className="text-[10px] text-[#757675] mb-1 block">Recurrence</label>
                <select value={showBlockCreate.recurring || "none"} onChange={e => setShowBlockCreate({...showBlockCreate, recurring: e.target.value})} data-testid="block-recurring"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm">
                  <option value="none">Does not repeat</option><option value="daily">Daily</option><option value="weekdays">Weekdays (Mon-Fri)</option><option value="weekly">Weekly</option>
                </select>
              </div>
              {showBlockCreate.recurring && showBlockCreate.recurring !== "none" && (
                <div><label className="text-[10px] text-[#757675] mb-1 block">Repeat until</label>
                <input type="date" value={showBlockCreate.recurring_end_date || ""} onChange={e => setShowBlockCreate({...showBlockCreate, recurring_end_date: e.target.value})}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" /></div>
              )}
              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={() => setShowBlockCreate(null)} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
                <button type="submit" data-testid="save-block-btn" className="bg-[#4A5568] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#374151]">Block time</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drag Confirmation Modal (Treatwell-style) */}
      {showDragConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="drag-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-[#1A1B1A] mb-4" style={H}>Confirm changes</h2>
            
            <div className="space-y-3">
              {/* Patient/Block name */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#205B5A]">
                    {showDragConfirm.isBlock ? "B" : showDragConfirm.appt?.patient_name?.charAt(0) || "A"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1B1A]">
                    {showDragConfirm.isBlock ? showDragConfirm.appt?.label : showDragConfirm.appt?.patient_name}
                  </p>
                  <p className="text-[11px] text-[#757675]">
                    {showDragConfirm.isBlock ? "Block time" : showDragConfirm.appt?.appointment_type_name}
                  </p>
                </div>
              </div>

              {/* Time change visualization */}
              <div className="bg-[#FAF9F6] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">From</p>
                    <p className="text-sm font-semibold text-[#1A1B1A] font-mono">{showDragConfirm.fromTime}</p>
                    <p className="text-[10px] text-[#757675]">{showDragConfirm.fromDate}</p>
                  </div>
                  <div className="flex items-center gap-2 px-4">
                    <div className="w-8 h-px bg-[#1A1B1A]/20" />
                    <ArrowRight className="w-4 h-4 text-[#205B5A]" />
                    <div className="w-8 h-px bg-[#1A1B1A]/20" />
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-[#757675] uppercase tracking-wider mb-1">To</p>
                    <p className="text-sm font-semibold text-[#205B5A] font-mono">{showDragConfirm.toTime}</p>
                    <p className="text-[10px] text-[#757675]">{showDragConfirm.toDate}</p>
                  </div>
                </div>
                
                {/* Practitioner change - only show if practitioner actually changed */}
                {showDragConfirm.practitionerChanged && (
                  <div className="mt-3 pt-3 border-t border-[#1A1B1A]/[0.06]">
                    <p className="text-[10px] text-[#757675] text-center">
                      Practitioner: <span className="font-medium text-[#1A1B1A]">
                        {practitioners.find(p => p.id === showDragConfirm.fromPracId)?.full_name?.split(" ")[0]}
                      </span>
                      {" → "}
                      <span className="font-medium text-[#205B5A]">
                        {practitioners.find(p => p.id === showDragConfirm.toPracId)?.full_name?.split(" ")[0]}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button 
                onClick={cancelDragChange}
                data-testid="drag-confirm-cancel"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-[#757675] hover:text-[#1A1B1A] hover:bg-[#FAF9F6] rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDragChange}
                data-testid="drag-confirm-save"
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#205B5A] hover:bg-[#174544] rounded-xl transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
